import { filterNewTransactions, transactionBankId } from "../shared/transaction-identity";
import { saveSyncResult, readSyncResult } from "../shared/sync-status";
/*
 * Bank Connector - Excel Add-in
 * Connects bank accounts via GoCardless and manages transactions in Excel
 */

// ============================================================================
// IMPORTS
// ============================================================================

import "../taskpane/taskpane.css";
import { consentExpiry, matchRenewedAccounts, RenewalAccount } from "../shared/renewal";
import {
    CONFIG,
    API_CONFIG,
    STATUS_MAP,
    CORE_COLUMNS
} from "../config";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface Bank {
    id: string;
    name: string;
    bic: string;
    transaction_total_days: string;
    countries: string[];
    logo?: string;
}

interface Requisition {
    id: string;
    link?: string;
    status?: string;
    institution_id?: string;
    agreement_details?: {
        valid_until?: string;
    };
}

interface ConnectedBank {
    id: string;
    bankName: string;
    bankLogo?: string;
    country: string;
    accountCount: number;
    connectedAt: string;
}

interface AccountMetadata {
    accountId: string;
    transactionAccountId: string;
    bankName: string;
    isSandbox: boolean;
    requisitionId: string;
}

interface MappingRule {
    typeFilter: string;
    identifier: string;
    category: string;
    subCategory: string;
}

interface AppState {
    selectedCountry: string | null;
    selectedBank: Bank | null;
    institutions: Bank[];
    currentRequisition: Requisition | null;
    connectedBanks: ConnectedBank[];
    isSandbox: boolean;
    licenseKey: string | null;
    isAuthCompleting: boolean;
    statusCache: Record<string, { data: unknown; timestamp: number }>;
    bankUrl?: string;
    currentView: string;
    language: "en" | "de";
}



// ============================================================================
// GLOBAL STATE
// ============================================================================

let currentState: AppState = {
    selectedCountry: null,
    selectedBank: null,
    institutions: [],
    currentRequisition: null,
    connectedBanks: [],
    isSandbox: false,
    licenseKey: null,
    isAuthCompleting: false,
    statusCache: {},
    currentView: 'connect',
    language: "en",
};

interface PendingRenewal {
    oldRequisitionId: string;
    bank: ConnectedBank;
    requisition: Requisition;
    institution: Bank;
    previousAccounts: RenewalAccount[];
}
let pendingRenewal: PendingRenewal | null = null;
let renewalStarting = false;
const RENEWAL_SETTING = "pendingBankRenewal";

let pollingInterval: ReturnType<typeof setInterval> | null = null;
let settingsModalCloseTimer: ReturnType<typeof setTimeout> | null = null;
const LANGUAGE_STORAGE_KEY = "bdm.language";
const LANGUAGE_SETTING_KEY = "uiLanguage";
let isTranslatingDom = false;
let domTranslationObserver: MutationObserver | null = null;

// Declare lucide on window for TypeScript
declare global {
    interface Window {
        lucide?: {
            createIcons: () => void;
        };
    }
}

const TEXT_DE: Record<string, string> = {
    "Cancel renewal": "Erneuerung abbrechen",
    "Renew bank access": "Bankzugriff erneuern",
    "Renewal in progress": "Erneuerung läuft",
    "Renewing bank access...": "Bankzugriff wird erneuert...",
    "Bank access renewed. Use Sync New Data to fetch transactions.": "Bankzugriff erneuert. Laden Sie Umsätze über Neue Daten synchronisieren.",
    "Bank Connector": "Bank Connector",
    "Settings": "Einstellungen",
    "System Ready": "System bereit",
    "Connect": "Verbinden",
    "Dashboard": "Dashboard",
    "Categories": "Kategorien",
    "Connect Your Bank": "Verbinden Sie Ihre Bank",
    "Choose a region or try demo mode to get started.": "Wählen Sie eine Region oder starten Sie mit dem Demomodus.",
    "Try Demo Mode": "Demomodus testen",
    "Explore features safely with simulated banking data.": "Funktionen sicher mit simulierten Bankdaten erkunden.",
    "or connect a real bank": "oder eine echte Bank verbinden",
    "more regions": "weitere Regionen",
    "Select Country": "Land auswählen",
    "Other Markets...": "Weitere Märkte...",
    "Popular": "Beliebt",
    "Europe (A-Z)": "Europa (A-Z)",
    "Germany": "Deutschland",
    "Austria": "Österreich",
    "Select Institution": "Institut auswählen",
    "Search for your bank or provider below.": "Suchen Sie unten nach Ihrer Bank oder Ihrem Anbieter.",
    "Search by name...": "Nach Name suchen...",
    "Loading institutions...": "Institute werden geladen...",
    "Back": "Zurück",
    "Configure Connection": "Verbindung konfigurieren",
    "Set your preferences and connect securely.": "Legen Sie Ihre Einstellungen fest und verbinden Sie sicher.",
    "Authorization Period": "Autorisierungszeitraum",
    "How long the link stays active.": "Wie lange der Link aktiv bleibt.",
    "90 days (Recommended)": "90 Tage (Empfohlen)",
    "30 days": "30 Tage",
    "7 days": "7 Tage",
    "1 day": "1 Tag",
    "Sync History": "Synchronisierungsverlauf",
    "How far back to fetch transactions.": "Wie weit Transaktionen rückwirkend geladen werden.",
    "Use maximum available": "Maximal verfügbaren Zeitraum verwenden",
    "Last 30 days": "Letzte 30 Tage",
    "Last 90 days": "Letzte 90 Tage",
    "Last 180 days": "Letzte 180 Tage",
    "Last year (365 days)": "Letztes Jahr (365 Tage)",
    "Last 2 years (730 days)": "Letzte 2 Jahre (730 Tage)",
    "Maximum available: -- days": "Maximal verfügbar: -- Tage",
    "Securely Connect": "Sicher verbinden",
    "Account Portfolio": "Kontenportfolio",
    "Overview of your connected financial data.": "Überblick über Ihre verbundenen Finanzdaten.",
    "Transactions": "Transaktionen",
    "Accounts": "Konten",
    "Last Synced": "Zuletzt synchronisiert",
    "Connected Banks": "Verbundene Banken",
    "Add": "Hinzufügen",
    "No banks connected yet": "Noch keine Banken verbunden",
    "Connect Your First Bank": "Erste Bank verbinden",
    "Sync New Data": "Neue Daten synchronisieren",
    "Subscription Required": "Abonnement erforderlich",
    "To connect to real banking institutions, you need an active subscription with a valid API key.": "Um echte Banken zu verbinden, benötigen Sie ein aktives Abonnement mit gültigem API-Schlüssel.",
    "Connect to 2,300+ institutions across 30 countries": "Verbindung zu 2.300+ Instituten in 30 Ländern",
    "Automatic transaction sync": "Automatische Transaktionssynchronisierung",
    "Secure PSD2-compliant connection": "Sichere PSD2-konforme Verbindung",
    "Up to 730 days of transaction history": "Bis zu 730 Tage Transaktionshistorie",
    "Contact Support for License Key": "Support für Lizenzschlüssel kontaktieren",
    "I Already Have a Key": "Ich habe bereits einen Schlüssel",
    "Live Connections Need a Key": "Live-Verbindungen benötigen einen Schlüssel",
    "Purchases are unavailable in this Office app. Enter an existing key in Settings or use Demo Mode.": "Käufe sind in dieser Office-App nicht verfügbar. Geben Sie in den Einstellungen einen vorhandenen Schlüssel ein oder nutzen Sie den Demomodus.",
    "You can still use": "Sie können weiterhin",
    "Demo Mode": "Demomodus",
    "to explore features with simulated data — no subscription needed!": "verwenden, um Funktionen mit simulierten Daten zu testen – kein Abo erforderlich!",
    "Back to Connect": "Zurück zu Verbinden",
    "Transaction Categories": "Transaktionskategorien",
    "Organize and categorize your transactions.": "Organisieren und kategorisieren Sie Ihre Transaktionen.",
    "Mapping Sheet": "Mapping-Tabelle",
    "Define rules to auto-categorize transactions.": "Definieren Sie Regeln zur automatischen Kategorisierung.",
    "Open Mapping Sheet": "Mapping-Tabelle öffnen",
    "Apply Categories": "Kategorien anwenden",
    "Run your mapping rules on all transactions.": "Wenden Sie Mapping-Regeln auf alle Transaktionen an.",
    "Categorize Transactions": "Transaktionen kategorisieren",
    "How it works": "So funktioniert es",
    "Add keywords in the Mapping sheet (e.g., \"Amazon\" → \"Shopping\"). Then click \"Categorize\" to apply rules to all transactions.": "Fügen Sie Stichwörter in der Mapping-Tabelle hinzu (z. B. \"Amazon\" → \"Einkaufen\"). Klicken Sie dann auf \"Kategorisieren\", um Regeln anzuwenden.",
    "Authorization Required": "Autorisierung erforderlich",
    "Copy Link": "Link kopieren",
    "Copy the authorization URL below.": "Kopieren Sie die untenstehende Autorisierungs-URL.",
    "Authorize in Browser": "Im Browser autorisieren",
    "Paste the link and log in to your bank.": "Fügen Sie den Link ein und melden Sie sich bei Ihrer Bank an.",
    "AUTHORIZATION URL": "AUTORISIERUNGS-URL",
    "Copy link": "Link kopieren",
    "Initializing...": "Initialisierung...",
    "Waiting for you to open the link...": "Es wird gewartet, dass Sie den Link öffnen...",
    "Already completed authorization?": "Autorisierung bereits abgeschlossen?",
    "I've Completed Authorization": "Ich habe die Autorisierung abgeschlossen",
    "Language": "Sprache",
    "Choose your display language for the add-in.": "Wählen Sie die Anzeigesprache für das Add-in.",
    "Subscription Key": "Abonnement-Schlüssel",
    "Not set": "Nicht gesetzt",
    "Enter key (e.g. sk_...)": "Schlüssel eingeben (z. B. sk_...)",
    "Required for connecting to live banking institutions.": "Erforderlich für Verbindungen zu echten Banken.",
    "Remove Key (Sign Out)": "Schlüssel entfernen (Abmelden)",
    "Cancel Subscription": "Abonnement kündigen",
    "Removing your key signs you out from live banking on this workbook/device.": "Das Entfernen Ihres Schlüssels meldet Sie für Live-Banking in dieser Arbeitsmappe/diesem Gerät ab.",
    "Need to stop billing? Use Cancel Subscription.": "Um die Abrechnung zu beenden, nutzen Sie Abonnement kündigen.",
    "Privacy Policy": "Datenschutzrichtlinie",
    "Terms of Service": "Nutzungsbedingungen",
    "Support": "Support",
    "System Alert": "Systemwarnung",
    "Dismiss": "Schließen",
    "No banks found for this country.": "Für dieses Land wurden keine Banken gefunden.",
    "Active": "Aktiv",
    "Invalid": "Ungültig",
    "Waiting for authorization...": "Warten auf Autorisierung...",
    "Connection Successful!": "Verbindung erfolgreich!",
    "Accounts linked. Completing setup...": "Konten verbunden. Einrichtung wird abgeschlossen...",
    "Link Expired": "Link abgelaufen",
    "Connection Rejected": "Verbindung abgelehnt",
    "Please enter a license key.": "Bitte einen Lizenzschlüssel eingeben.",
    "No key entered. You can continue using Demo Mode.": "Kein Schlüssel eingegeben. Sie können den Demomodus weiterhin nutzen.",
    "Remove saved key?": "Gespeicherten Schlüssel entfernen?",
    "This will sign you out from live banking in this workbook.": "Dadurch werden Sie in dieser Arbeitsmappe vom Live-Banking abgemeldet.",
    "This only removes the key from Excel. Subscription billing remains active until you cancel it.": "Dies entfernt nur den Schlüssel aus Excel. Die Abrechnung bleibt aktiv, bis Sie kündigen.",
    "No saved license key found.": "Kein gespeicherter Lizenzschlüssel gefunden.",
    "Signed out. License key removed.": "Abgemeldet. Lizenzschlüssel entfernt.",
    "Ready": "Bereit",
    "Loading banks...": "Banken werden geladen...",
    "Select a bank": "Wählen Sie eine Bank aus",
    "Error loading banks": "Fehler beim Laden der Banken",
    "Loading sandbox bank...": "Sandbox-Bank wird geladen...",
    "Select sandbox bank": "Sandbox-Bank auswählen",
    "Error loading sandbox bank": "Fehler beim Laden der Sandbox-Bank",
    "Configure connection": "Verbindung konfigurieren",
    "No bank selected": "Keine Bank ausgewählt",
    "Creating bank connection...": "Bankverbindung wird erstellt...",
    "Connection failed": "Verbindung fehlgeschlagen",
    "Copy link and complete authorization in browser": "Link kopieren und Autorisierung im Browser abschließen",
    "Link copied! Open it in your browser": "Link kopiert! Im Browser öffnen",
    "Please manually copy the link above": "Bitte den obigen Link manuell kopieren",
    "✅ Connection successful! Fetching transactions...": "✅ Verbindung erfolgreich! Transaktionen werden geladen...",
    "Finalizing setup...": "Einrichtung wird abgeschlossen...",
    "Bank connected successfully!": "Bank erfolgreich verbunden!",
    "Authorization failed": "Autorisierung fehlgeschlagen",
    "Refreshing data...": "Daten werden aktualisiert...",
    "Refresh failed": "Aktualisierung fehlgeschlagen",
    "Removing connection...": "Verbindung wird entfernt...",
    "Connection removed successfully.": "Verbindung erfolgreich entfernt.",
    "Redirecting to bank renewal...": "Weiterleitung zur Bankverlängerung...",
    "Applying mapping rules...": "Mapping-Regeln werden angewendet...",
    "Error applying categories": "Fehler beim Anwenden der Kategorien",
    "Opening mapping sheet...": "Mapping-Tabelle wird geöffnet...",
    "Mapping sheet ready! Add your rules and click \"Categorize\"": "Mapping-Tabelle bereit! Regeln hinzufügen und auf \"Kategorisieren\" klicken",
    "Error opening mapping sheet": "Fehler beim Öffnen der Mapping-Tabelle",
    "Validating license...": "Lizenz wird geprüft...",
    "License saved successfully!": "Lizenz erfolgreich gespeichert!",
    "An unknown error occurred.": "Ein unbekannter Fehler ist aufgetreten.",
    "Show technical details": "Technische Details anzeigen",
    "Created": "Erstellt",
    "Giving Consent": "Zustimmung erteilen",
    "Authenticating": "Authentifizierung",
    "Rejected": "Abgelehnt",
    "Selecting Accounts": "Konten auswählen",
    "Granting Access": "Zugriff wird erteilt",
    "Linked": "Verbunden",
    "Expired": "Abgelaufen",
    "Requisition created...": "Anfrage erstellt...",
    "Accept consent on the next screen...": "Bitte Zustimmung im nächsten Schritt erteilen...",
    "Please finish login in your bank app...": "Bitte Login in Ihrer Banking-App abschließen...",
    "Authorization was rejected.": "Autorisierung wurde abgelehnt.",
    "Please choose which accounts to link...": "Bitte wählen Sie die zu verbindenden Konten...",
    "Finalizing bank permissions...": "Bankberechtigungen werden abgeschlossen...",
    "Successfully connected!": "Erfolgreich verbunden!",
    "Link has expired.": "Link ist abgelaufen.",
    "Processing...": "Verarbeitung...",
    "Still processing...": "Noch in Verarbeitung...",
    "Creating connection...": "Verbindung wird erstellt...",
    "Refreshing...": "Wird aktualisiert...",
    "Applying...": "Wird angewendet...",
    "Opening...": "Wird geöffnet...",
    "Checking...": "Wird geprüft...",
};

function getLanguageFromBrowser(): "en" | "de" {
    if (typeof navigator === "undefined") return "en";
    return navigator.language.toLowerCase().startsWith("de") ? "de" : "en";
}

async function loadLanguagePreference(): Promise<void> {
    let lang: "en" | "de" = getLanguageFromBrowser();
    const localValue = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (localValue === "en" || localValue === "de") {
        lang = localValue;
    }

    try {
        Office.context.document.settings.refreshAsync(() => {
            const officeValue = Office.context.document.settings.get(LANGUAGE_SETTING_KEY) as string | null;
            if (officeValue === "en" || officeValue === "de") {
                lang = officeValue;
            }
            currentState.language = lang;
            applyLanguagePreference();
        });
    } catch {
        currentState.language = lang;
        applyLanguagePreference();
    }
}

function saveLanguagePreference(lang: "en" | "de"): void {
    const previous = currentState.language;
    currentState.language = lang;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    try {
        Office.context.document.settings.set(LANGUAGE_SETTING_KEY, lang);
        Office.context.document.settings.saveAsync();
    } catch {
        // non-fatal in browser context
    }
    applyLanguagePreference();
    if (previous === "de" && lang === "en") {
        window.location.reload();
    }
}

function translateFreeText(input: string): string {
    if (currentState.language !== "de" || !input) return input;

    const exact = TEXT_DE[input];
    if (exact) return exact;

    return input
        .replace(/^Selected ([A-Z]{2,8})\. Loading banks\.\.\.$/, "Ausgewählt: $1. Banken werden geladen...")
        .replace(/^Using (\d+) days of history and (\d+) days of access$/, "Verwende $1 Tage Verlauf und $2 Tage Zugriff")
        .replace(/^Refreshing account (\d+) of (\d+)\.\.\.$/, "Konto $1 von $2 wird synchronisiert...")
        .replace(/^Bank is busy \(409\)\. Retrying (\d+)\/(\d+)\.\.\.$/, "Bank ist ausgelastet (409). Neuer Versuch $1/$2...")
        .replace(/^Applied categories to (\d+) transactions!$/, "Kategorien auf $1 Transaktionen angewendet!")
        .replace(/^Found (\d+) accounts. Fetching transactions\.\.\.$/, "$1 Konten gefunden. Transaktionen werden geladen...")
        .replace(/^Saving (\d+) transactions to Excel\.\.\.$/, "$1 Transaktionen werden in Excel gespeichert...")
        .replace(/^Imported (\d+) transactions\.$/, "$1 Transaktionen importiert.")
        .replace(/^Refresh complete! Imported (\d+) new transactions\.$/, "Aktualisierung abgeschlossen! $1 neue Transaktionen importiert.")
        .replace(/^Failed to load banks: (.+)$/, "Banken konnten nicht geladen werden: $1")
        .replace(/^Failed to load sandbox bank: (.+)$/, "Sandbox-Bank konnte nicht geladen werden: $1")
        .replace(/^Failed to create bank connection: (.+)$/, "Bankverbindung konnte nicht erstellt werden: $1")
        .replace(/^Failed to complete authorization: (.+)$/, "Autorisierung konnte nicht abgeschlossen werden: $1")
        .replace(/^Failed to refresh data: (.+)$/, "Datenaktualisierung fehlgeschlagen: $1")
        .replace(/^Failed to delete connection: (.+)$/, "Verbindung konnte nicht gelöscht werden: $1")
        .replace(/^Failed to apply categories: (.+)$/, "Kategorien konnten nicht angewendet werden: $1")
        .replace(/^Failed to open mapping sheet: (.+)$/, "Mapping-Tabelle konnte nicht geöffnet werden: $1")
        .replace(/^Failed to validate license: (.+)$/, "Lizenzprüfung fehlgeschlagen: $1")
        .replace(/^Failed to remove license key: (.+)$/, "Lizenzschlüssel konnte nicht entfernt werden: $1")
        .replace(/^Current status: ([^\n]+)\n\n([^\n]+)\n\nPlease complete the authorization in your browser first\.$/, "Aktueller Status: $1\n\n$2\n\nBitte schließen Sie zuerst die Autorisierung im Browser ab.");
}

function applyTextIfMapped(textNode: Text): void {
    const raw = textNode.nodeValue || "";
    const trimmed = raw.trim();
    if (!trimmed) return;

    const translated = translateFreeText(trimmed);
    if (translated === trimmed) return;

    const start = raw.indexOf(trimmed);
    const end = start + trimmed.length;
    textNode.nodeValue = `${raw.slice(0, start)}${translated}${raw.slice(end)}`;
}

function translateElementAttributes(el: Element): void {
    const htmlEl = el as HTMLElement;
    const title = htmlEl.getAttribute("title");
    if (title) {
        htmlEl.setAttribute("title", translateFreeText(title));
    }

    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        if (el.placeholder) {
            el.placeholder = translateFreeText(el.placeholder);
        }
    }
}

function translateDomSubtree(root: ParentNode): void {
    if (currentState.language === "en") return;
    if (isTranslatingDom) return;

    isTranslatingDom = true;
    try {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node = walker.nextNode();
        while (node) {
            applyTextIfMapped(node as Text);
            node = walker.nextNode();
        }

        if (root instanceof Element) {
            translateElementAttributes(root);
            root.querySelectorAll("*").forEach(translateElementAttributes);
        } else if (root === document) {
            document.querySelectorAll("*").forEach(translateElementAttributes);
        }
    } finally {
        isTranslatingDom = false;
    }
}

function startDomTranslationObserver(): void {
    if (domTranslationObserver) {
        domTranslationObserver.disconnect();
    }

    domTranslationObserver = new MutationObserver((mutations) => {
        if (currentState.language !== "de") return;
        mutations.forEach((mutation) => {
            if (mutation.type === "childList") {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        applyTextIfMapped(node as Text);
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                        translateDomSubtree(node as Element);
                    }
                });
            } else if (mutation.type === "characterData") {
                applyTextIfMapped(mutation.target as Text);
            }
        });
    });

    domTranslationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
    });
}

function applyLanguagePreference(): void {
    const htmlEl = document.documentElement;
    htmlEl.lang = currentState.language;

    const languageSelect = document.getElementById("languageSelect") as HTMLSelectElement | null;
    if (languageSelect) {
        languageSelect.value = currentState.language;
    }

    if (currentState.language === "de") {
        translateDomSubtree(document);
    }

    applyCommerceRestrictions();
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function isCommerceRestrictedPlatform(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /android|iphone|ipad|ipod/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    let officeCommerceBlocked = false;
    try {
        officeCommerceBlocked = Office.context?.commerceAllowed === false;
    } catch {
        officeCommerceBlocked = false;
    }

    return isMobile || officeCommerceBlocked;
}

function applyCommerceRestrictions(): void {
    if (!isCommerceRestrictedPlatform()) return;

    document.querySelectorAll(".commerce-only").forEach((el) => {
        (el as HTMLElement).style.display = "none";
    });

    const subscribeTitle = document.getElementById("subscribeTitle");
    if (subscribeTitle) {
        subscribeTitle.textContent = translateFreeText("Live Connections Need a Key");
    }

    const subscribeDesc = document.getElementById("subscribeDesc");
    if (subscribeDesc) {
        subscribeDesc.textContent = translateFreeText("Purchases are unavailable in this Office app. Enter an existing key in Settings or use Demo Mode.");
    }
}

function getExcelColumnLetter(columnNumber: number): string {
    let columnLetter = "";
    while (columnNumber > 0) {
        const remainder = (columnNumber - 1) % 26;
        columnLetter = String.fromCharCode(65 + remainder) + columnLetter;
        columnNumber = Math.floor((columnNumber - 1) / 26);
    }
    return columnLetter;
}

function safeExtract(obj: Record<string, unknown>, path: string, defaultValue: unknown = ""): unknown {
    try {
        return path.split(".").reduce((current: unknown, key: string) => {
            if (current && typeof current === 'object' && key in current) {
                return (current as Record<string, unknown>)[key];
            }
            return defaultValue;
        }, obj);
    } catch {
        return defaultValue;
    }
}

function flattenObject(obj: Record<string, unknown>, prefix = "", res: Record<string, unknown> = {}): Record<string, unknown> {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const propName = prefix ? `${prefix}_${key}` : key;
            const value = obj[key];
            if (typeof value === "object" && value !== null && !Array.isArray(value)) {
                flattenObject(value as Record<string, unknown>, propName, res);
            } else if (Array.isArray(value)) {
                res[propName] = value.join("; ");
            } else {
                res[propName] = value;
            }
        }
    }
    return res;
}

function extractTransactionData(tx: Record<string, unknown>, bankName = "Unknown Bank"): Record<string, unknown> {
    const amountData = (tx.transactionAmount as Record<string, unknown>) || { amount: tx.amount || 0, currency: tx.currency || "" };
    const amount = parseFloat(String(amountData.amount || 0));
    const currency = String(amountData.currency || "");

    const creditorName = String(tx.creditorName || safeExtract(tx, "creditor.name") || "");
    const debtorName = String(tx.debtorName || safeExtract(tx, "debtor.name") || "");

    let counterpartyName = "Unknown";
    if (amount < 0) {
        counterpartyName = creditorName || "Unknown Creditor";
    } else {
        counterpartyName = debtorName || "Unknown Debtor";
    }

    const description = String(safeExtract(tx, "remittanceInformationUnstructured") || safeExtract(tx, "description") || counterpartyName);
    const bookingDate = String(safeExtract(tx, "bookingDate") || safeExtract(tx, "date") || "");
    const transactionId = transactionBankId(tx);
    const accountId = String(safeExtract(tx, "resourceId") || safeExtract(tx, "accountId") || "");
    const accountName = String(safeExtract(tx, "name") || safeExtract(tx, "account.name") || "");
    const uniqueKey = `${transactionId}_${bookingDate}_${amount}_${accountId}`;

    const creditorIban = String(safeExtract(tx, "creditorAccount.iban") || "");
    const debtorIban = String(safeExtract(tx, "debtorAccount.iban") || "");
    const counterpartyIban = amount < 0 ? creditorIban : debtorIban;

    const flattenedData = flattenObject(tx);

    const redundantFields = [
        "transactionId", "id", "entryReference",
        "bookingDate", "date",
        "amount", "currency",
        "resourceId", "accountId",
        "remittanceInformationUnstructured"
    ];
    redundantFields.forEach(field => delete flattenedData[field]);

    const coreData: Record<string, unknown> = {
        "Booking Date": bookingDate,
        "Amount": amount,
        "Currency": currency,
        "Description": description,
        "Creditor/Debtor": counterpartyName,
        "Type": amount < 0 ? "Creditor" : "Debtor",
        "Category": "",
        "Sub Category": "",
        "IBAN": counterpartyIban,
        "Bank Name": bankName,
        "Account Name": accountName,
        "Transaction ID": transactionId,
        "Unique Key": uniqueKey,
        "Account IBAN": String(tx.accountIban || tx.iban || ""),
    };

    return { ...flattenedData, ...coreData };
}

// Helper for better Office.js error handling
function logOfficeError(error: unknown, context: string): void {
    console.error(`${context}:`, error);
    if (error instanceof OfficeExtension.Error) {
        console.error("Office Debug Info:", JSON.stringify(error.debugInfo));
    }
}

// ============================================================================
// OFFICE.JS INITIALIZATION
// ============================================================================

Office.onReady((info) => {
    if (info.host === Office.HostType.Excel) {
        initializeApp();
    }
});

function initializeApp(): void {
    setupEventListeners();
    setInterval(() => { void updateCommandNotice(); }, 5000);
    startDomTranslationObserver();
    switchView('connect');
    void loadLanguagePreference();
    updateStatus(translateFreeText("Ready"));
    applyCommerceRestrictions();
    if (window.lucide) window.lucide.createIcons();

    setTimeout(async () => {
        console.log("App Initialization: Loading data...");
        try {
            await loadLicenseKey();
            pendingRenewal = Office.context.document.settings.get(RENEWAL_SETTING) || null;
            await loadConnectedBanks();
            console.log("App Initialization: Success");

            // Check if we have connected banks, show dashboard
            const banks = await getConnectedBanksFromConfig();
            if (banks.length > 0) {
                switchView('dashboard');
            }
        } catch (error) {
            console.error("Critical Error during initialization:", error);
            showError("Initialization issue: " + (error as Error).message);
        }
    }, 100);
}

function setupEventListeners(): void {
    const languageSelect = document.getElementById("languageSelect") as HTMLSelectElement;
    if (languageSelect) {
        languageSelect.addEventListener("change", (e) => {
            const selected = (e.target as HTMLSelectElement).value;
            if (selected === "en" || selected === "de") {
                saveLanguagePreference(selected);
                updateStatus(translateFreeText("Ready"));
            }
        });
    }

    // Country selection buttons
    document.querySelectorAll(".country-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => selectCountry(btn as HTMLElement));
    });

    // Country selection dropdown
    const countryDropdown = document.getElementById("countryDropdown") as HTMLSelectElement;
    if (countryDropdown) {
        countryDropdown.addEventListener("change", (e) => {
            const countryCode = (e.target as HTMLSelectElement).value;
            if (countryCode) {
                // Clear any button highlights
                document.querySelectorAll(".country-btn").forEach(btn => btn.classList.remove("selected"));
                selectCountryByCode(countryCode);
            }
        });
    }

    // Sandbox selection
    document.querySelectorAll(".sandbox-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => selectSandbox((e.target as HTMLElement).closest(".sandbox-btn, .card-highlight") as HTMLElement));
    });

    // Bank search
    const bankSearch = document.getElementById("bankSearch") as HTMLInputElement;
    if (bankSearch) {
        bankSearch.addEventListener("input", (e) => filterBanks((e.target as HTMLInputElement).value));
    }

    // Connect button
    const connectBtn = document.getElementById("connectBtn");
    if (connectBtn) {
        connectBtn.addEventListener("click", initiateConnection);
    }

    // Modal buttons
    const copyLinkBtn = document.getElementById("copyLinkBtn");
    if (copyLinkBtn) {
        copyLinkBtn.addEventListener("click", copyBankLink);
    }
}

// ============================================================================
// VIEW NAVIGATION
// ============================================================================

function switchView(viewName: string): void {
    currentState.currentView = viewName;

    // Update nav tabs
    document.querySelectorAll('.nav-tab').forEach(tab => {
        tab.classList.remove('active');
        if (tab.getAttribute('data-view') === viewName) {
            tab.classList.add('active');
        }
    });

    // Update views
    document.querySelectorAll('.view').forEach(view => {
        view.classList.remove('active');
    });

    const targetView = document.getElementById(`view-${viewName}`);
    if (targetView) {
        targetView.classList.add('active');
    }

    // View-specific actions
    if (viewName === 'dashboard') {
        loadConnectedBanks();
        updateDataSummary();
    } else if (viewName === 'connect') {
        goToConnectStep(1);
    }

    if (window.lucide) window.lucide.createIcons();
}

function goToConnectStep(stepNumber: number): void {
    document.querySelectorAll('#view-connect .sub-view').forEach(step => {
        step.classList.remove('active');
    });

    const targetStep = document.getElementById(`connect-step${stepNumber}`);
    if (targetStep) {
        targetStep.classList.add('active');
    }

    if (window.lucide) window.lucide.createIcons();
}

// Legacy navigation functions for compatibility
function goToStep(stepNumber: number): void {
    if (stepNumber === 4) {
        switchView('dashboard');
    } else if (stepNumber === 1) {
        switchView('connect');
        goToConnectStep(1);
    } else if (stepNumber === 2) {
        switchView('connect');
        goToConnectStep(2);
    } else if (stepNumber === 3) {
        switchView('connect');
        goToConnectStep(3);
    }
}

function resetAndGoToStep1(): void {
    console.log("Resetting state and going to Connect view");
    currentState.selectedCountry = null;
    currentState.selectedBank = null;
    currentState.currentRequisition = null;
    currentState.isSandbox = false;

    const selectedBankInfo = document.getElementById("selectedBankInfo");
    if (selectedBankInfo) selectedBankInfo.innerHTML = "";

    const connectBtn = document.getElementById("connectBtn");
    if (connectBtn) {
        connectBtn.innerHTML = `<i data-lucide="link"></i> ${translateFreeText("Securely Connect")}`;
        (connectBtn as HTMLButtonElement).disabled = false;
    }

    switchView('connect');
    goToConnectStep(1);
}

// ============================================================================
// STEP 1: COUNTRY/SANDBOX SELECTION
// ============================================================================

function selectCountry(countryBtn: HTMLElement): void {
    const countryCode = countryBtn.dataset.country;
    if (!countryCode) return;

    // Highlight the button
    document.querySelectorAll(".country-btn, .sandbox-btn, .card-highlight").forEach((btn) => {
        btn.classList.remove("selected");
    });
    countryBtn.classList.add("selected");

    // Reset dropdown if it exists
    const countryDropdown = document.getElementById("countryDropdown") as HTMLSelectElement;
    if (countryDropdown) countryDropdown.value = "";

    selectCountryByCode(countryCode);
}

function selectCountryByCode(countryCode: string): void {
    currentState.selectedCountry = countryCode;
    currentState.isSandbox = false;

    // Pulse feedback or status update
    updateStatus(`Selected ${countryCode}. Loading banks...`);

    setTimeout(() => {
        if (currentState.selectedCountry) {
            loadBanks(currentState.selectedCountry);
        }
        goToConnectStep(2);
    }, 300);
}

function selectSandbox(sandboxBtn: HTMLElement): void {
    document.querySelectorAll(".country-btn, .sandbox-btn, .card-highlight").forEach((btn) => {
        btn.classList.remove("selected");
    });

    sandboxBtn.classList.add("selected");
    currentState.selectedCountry = "SANDBOX";
    currentState.isSandbox = true;

    setTimeout(() => {
        loadSandboxBank();
        goToConnectStep(2);
    }, 300);
}

// ============================================================================
// STEP 2: BANK SELECTION
// ============================================================================

async function loadBanks(countryCode: string): Promise<void> {
    const banksLoading = document.getElementById("banksLoading");
    const banksList = document.getElementById("banksList");

    try {
        if (banksLoading) banksLoading.classList.remove("hidden");
        if (banksList) banksList.innerHTML = "";
        updateStatus("Loading banks...");

        const response = await fetch(`${CONFIG.WORKER_URL}/institutions?country=${countryCode}`);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const institutions: Bank[] = await response.json();
        currentState.institutions = institutions;

        if (banksLoading) banksLoading.classList.add("hidden");
        renderBanks(institutions);
        updateStatus("Select a bank");
    } catch (error) {
        console.error("Error loading banks:", error);
        if (banksLoading) banksLoading.classList.add("hidden");
        showError("Failed to load banks: " + (error as Error).message);
        updateStatus("Error loading banks");
    }
}

function loadSandboxBank(): void {
    const banksLoading = document.getElementById("banksLoading");
    const banksList = document.getElementById("banksList");

    try {
        if (banksLoading) banksLoading.classList.remove("hidden");
        if (banksList) banksList.innerHTML = "";
        updateStatus("Loading sandbox bank...");

        const sandboxBank: Bank = {
            id: "SANDBOXFINANCE_SFIN0000",
            name: "Sandbox Finance",
            bic: "SFIN0000",
            transaction_total_days: "90",
            countries: ["SANDBOX"],
            logo: "https://via.placeholder.com/48x48/6366f1/ffffff?text=SB",
        };

        currentState.institutions = [sandboxBank];

        if (banksLoading) banksLoading.classList.add("hidden");
        renderBanks([sandboxBank]);
        updateStatus("Select sandbox bank");
    } catch (error) {
        console.error("Error loading sandbox bank:", error);
        if (banksLoading) banksLoading.classList.add("hidden");
        showError("Failed to load sandbox bank: " + (error as Error).message);
        updateStatus("Error loading sandbox bank");
    }
}

function renderBanks(institutions: Bank[]): void {
    const banksList = document.getElementById("banksList");
    if (!banksList) return;

    if (!institutions || institutions.length === 0) {
        banksList.innerHTML = '<div class="no-results">No banks found for this country.</div>';
        return;
    }

    banksList.innerHTML = institutions
        .map((bank) => {
            const isSandbox = bank.id.includes("SANDBOX");
            const logoHtml = isSandbox
                ? `<i data-lucide='test-tube-2' style='width: 24px; color: var(--accent);'></i>`
                : (bank.logo ? `<img src="${bank.logo}" alt="${bank.name}" onerror="this.parentElement.innerHTML='<i data-lucide=&quot;landmark&quot; style=&quot;width: 24px; color: var(--text-muted);&quot;></i>'">` : `<i data-lucide='landmark' style='width: 24px; color: var(--text-muted);'></i>`);

            return `
                <div class="bank-item" onclick="selectBank('${bank.id}')">
                    <div class="bank-logo">
                        ${logoHtml}
                    </div>
                    <div class="bank-details">
                        <div class="bank-name">${bank.name}</div>
                        <div class="bank-info">${currentState.language === "de"
                    ? `${bank.transaction_total_days} Tage Verlauf${isSandbox ? " (Demo)" : ""}`
                    : `${bank.transaction_total_days} days history${isSandbox ? " (Demo)" : ""}`}</div>
                    </div>
                </div>
            `;
        })
        .join("");

    if (window.lucide) window.lucide.createIcons();
}

function filterBanks(searchTerm: string): void {
    const filtered = currentState.institutions.filter(
        (bank) =>
            bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bank.bic.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    renderBanks(filtered);
}

function selectBank(bankId: string): void {
    const bank = currentState.institutions.find((b) => b.id === bankId);
    if (!bank) return;

    currentState.selectedBank = bank;

    const connectBtn = document.getElementById("connectBtn") as HTMLButtonElement;
    if (connectBtn) {
        connectBtn.innerHTML = `<i data-lucide="link"></i> ${translateFreeText("Securely Connect")}`;
        connectBtn.disabled = false;
    }

    const isSandbox = bank.id.includes("SANDBOX");
    const logoHtml = isSandbox
        ? `<div class="bank-logo"><i data-lucide="test-tube-2" style="width: 28px; color: var(--accent);"></i></div>`
        : (bank.logo ? `<img src="${bank.logo}" alt="${bank.name}" class="bank-logo" onerror="this.style.display='none'">` : `<div class="bank-logo"><i data-lucide="landmark" style="width: 28px; color: var(--text-muted);"></i></div>`);

    const selectedBankInfo = document.getElementById("selectedBankInfo");
    if (selectedBankInfo) {
        selectedBankInfo.innerHTML = `
            <div class="selected-bank">
                ${logoHtml}
                <div>
                    <h4>${bank.name}</h4>
                    <p>${currentState.language === "de"
                ? `Max. Verlauf: ${bank.transaction_total_days} Tage`
                : `Max history: ${bank.transaction_total_days} days`}</p>
                </div>
            </div>
        `;
    }

    if (window.lucide) window.lucide.createIcons();
    updateHistoricalDataOptions(bank.transaction_total_days);
    goToConnectStep(3);
    updateStatus("Configure connection");
}

function updateHistoricalDataOptions(maxDays: string): void {
    const maxDaysText = document.getElementById("maxDaysText");
    const historicalDaysSelect = document.getElementById("historicalDays") as HTMLSelectElement;

    if (maxDaysText) {
        const span = maxDaysText.querySelector('span');
        if (span) {
            span.textContent = currentState.language === "de" ? `Maximal verfügbar: ${maxDays} Tage` : `Maximum available: ${maxDays} days`;
        }
    }

    if (historicalDaysSelect) {
        const autoOption = historicalDaysSelect.querySelector('option[value="auto"]');
        if (autoOption) {
            autoOption.textContent = currentState.language === "de"
                ? `Maximal verfügbar verwenden (${maxDays} Tage)`
                : `Use maximum available (${maxDays} days)`;
        }

        const options = historicalDaysSelect.querySelectorAll("option");
        options.forEach((option) => {
            const value = parseInt(option.value);
            if (!isNaN(value) && value > parseInt(maxDays)) {
                option.disabled = true;
                if (!option.textContent?.includes("(Not available")) {
                    option.textContent += currentState.language === "de" ? " (Nicht verfügbar)" : " (Not available)";
                }
            } else if (!isNaN(value)) {
                option.disabled = false;
                option.textContent = option.textContent
                    ?.replace(" (Not available)", "")
                    .replace(" (Nicht verfügbar)", "") || option.textContent;
            }
        });

        historicalDaysSelect.value = "auto";
    }
}

// ============================================================================
// STEP 3: BANK CONNECTION
// ============================================================================

async function initiateConnection(): Promise<void> {
    if (pendingRenewal || renewalStarting) {
        showError("Finish the pending bank renewal from the Dashboard first.");
        return;
    }
    console.log("Connect button clicked");
    const connectBtn = document.getElementById("connectBtn") as HTMLButtonElement;

    const accessDaysEl = document.getElementById("accessDays") as HTMLSelectElement;
    const historicalDaysEl = document.getElementById("historicalDays") as HTMLSelectElement;

    const accessDays = accessDaysEl ? accessDaysEl.value : "90";
    const historicalDays = historicalDaysEl ? historicalDaysEl.value : "auto";

    if (!currentState.selectedBank) {
        showError("No bank selected");
        return;
    }

    // Check if user has a valid license key for real banks (not sandbox)
    if (!currentState.isSandbox && !currentState.licenseKey) {
        switchView('subscribe');
        return;
    }

    try {
        connectBtn.innerHTML = `⏳ ${translateFreeText("Creating connection...")}`;
        connectBtn.disabled = true;
        updateStatus("Creating bank connection...");

        let actualHistoricalDays: number;
        let actualAccessDays = parseInt(accessDays);

        const bankMaxHistory = parseInt(currentState.selectedBank.transaction_total_days) || 90;

        if (currentState.isSandbox) {
            actualHistoricalDays = 90;
            actualAccessDays = Math.min(actualAccessDays, 90);
        } else {
            if (historicalDays === "auto") {
                actualHistoricalDays = bankMaxHistory;
            } else {
                const userDays = parseInt(historicalDays);
                actualHistoricalDays = Math.min(userDays, bankMaxHistory);
            }
            actualAccessDays = Math.min(actualAccessDays, 180);
        }

        console.log(`Using ${actualHistoricalDays} days of history and ${actualAccessDays} days of access`);

        const linkData = {
            institution_id: currentState.selectedBank.id,
            bank_name: currentState.selectedBank.name,
            max_historical_days: actualHistoricalDays,
            access_valid_for_days: actualAccessDays,
            access_scope: ["balances", "details", "transactions"],
            redirect: CONFIG.REDIRECT_URL,
            user_language: currentState.language.toUpperCase(),
            reference: `excel_${Date.now()}`,
        };

        const response = await fetch(`${CONFIG.WORKER_URL}/link-builder`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-License-Key": currentState.licenseKey || ""
            },
            body: JSON.stringify(linkData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || `HTTP ${response.status}`);
        }

        const requisitionData: Requisition = await response.json();
        currentState.currentRequisition = requisitionData;

        showConnectionModal(requisitionData.link || "");
    } catch (error) {
        console.error("Error creating connection:", error);
        showError("Failed to create bank connection: " + (error as Error).message);
        updateStatus("Connection failed");

        connectBtn.innerHTML = `<i data-lucide="link"></i> ${translateFreeText("Securely Connect")}`;
        connectBtn.disabled = false;
        if (window.lucide) window.lucide.createIcons();
    }
}

function showConnectionModal(bankUrl: string): void {
    document.getElementById('cancelRenewalBtn')?.classList.toggle('hidden', !pendingRenewal);
    currentState.bankUrl = bankUrl;
    const bankLinkInput = document.getElementById("bankLinkInput") as HTMLInputElement;
    if (bankLinkInput) {
        bankLinkInput.value = bankUrl;
    }
    const modal = document.getElementById("connectionModal");
    if (modal) modal.classList.remove("hidden");

    const manualButton = document.getElementById('manualCompleteBtn') as HTMLButtonElement;
    if (manualButton) {
        manualButton.disabled = false;
        manualButton.textContent = translateFreeText("I've Completed Authorization");
    }
    if (currentState.currentRequisition && currentState.currentRequisition.id) {
        startPollingStatus(currentState.currentRequisition.id);
    }

    updateStatus("Copy link and complete authorization in browser");
    if (window.lucide) window.lucide.createIcons();
}

function copyBankLink(): void {
    const bankLinkInput = document.getElementById("bankLinkInput") as HTMLInputElement;
    const copyBtn = document.getElementById("copyLinkBtn") as HTMLButtonElement;

    if (bankLinkInput) {
        bankLinkInput.select();
        bankLinkInput.setSelectionRange(0, 99999);

        try {
            document.execCommand("copy");

            const originalHtml = copyBtn.innerHTML;
            copyBtn.innerHTML = '<i data-lucide="check"></i>';
            copyBtn.style.background = "var(--success)";
            copyBtn.style.borderColor = "var(--success)";

            setTimeout(() => {
                copyBtn.innerHTML = originalHtml;
                copyBtn.style.background = "";
                copyBtn.style.borderColor = "";
                if (window.lucide) window.lucide.createIcons();
            }, 2000);

            updateStatus("Link copied! Open it in your browser");
            if (window.lucide) window.lucide.createIcons();
        } catch (err) {
            console.error("Failed to copy link:", err);
            updateStatus("Please manually copy the link above");
        }
    }
}

async function completeAuthorization(): Promise<void> {
    if (pendingRenewal) {
        await completeRenewal();
        return;
    }
    if (currentState.isAuthCompleting) return;
    currentState.isAuthCompleting = true;

    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }

    const connectBtn = document.getElementById("connectBtn");

    closeModal("connectionModal");
    updateStatus("✅ Connection successful! Fetching transactions...");
    if (connectBtn) connectBtn.innerHTML = "✅ Connection successful!";
    setProgress(5);

    try {
        await new Promise((resolve) => setTimeout(resolve, 800));

        const msgAccounts = "Identifying accounts...";
        updateStatus(msgAccounts);
        if (connectBtn) connectBtn.innerHTML = `⏳ ${msgAccounts}`;
        setProgress(25);

        const accountsData = await fetchAccounts();
        const msgSaving = `Found ${accountsData.accounts.length} accounts. Saving...`;
        updateStatus(msgSaving);
        if (connectBtn) connectBtn.innerHTML = `⏳ ${msgSaving}`;
        setProgress(50);

        await saveAccountsToConfig(accountsData);
        setProgress(75);

        const msgTx = "Fetching transactions...";
        updateStatus(msgTx);
        if (connectBtn) connectBtn.innerHTML = `⏳ ${msgTx}`;
        await fetchAndSaveTransactions();
        setProgress(90);

        updateStatus("Finalizing setup...");
        await createMappingSheet();
        setProgress(100);

        if (currentState.currentRequisition && currentState.selectedBank && currentState.selectedCountry) {
            currentState.connectedBanks.push({
                id: currentState.currentRequisition.id,
                bankName: currentState.selectedBank.name,
                bankLogo: currentState.selectedBank.logo,
                country: currentState.selectedCountry,
                accountCount: accountsData.accounts.length,
                connectedAt: new Date().toISOString(),
            });
        }

        updateStatus("Bank connected successfully!");
        setTimeout(() => {
            switchView('dashboard');
            loadConnectedBanks();
            setProgress(0);
            currentState.isAuthCompleting = false;
        }, 1500);
    } catch (error) {
        await saveSyncResult(false, (error as Error).message).catch(e => console.error("Could not save failure status:", e));
        console.error("Error completing authorization:", error);
        showError("Failed to complete authorization: " + (error as Error).message);
        updateStatus("Authorization failed");
        setProgress(0);
        currentState.isAuthCompleting = false;
    }
}

// ============================================================================
// DATA MANAGEMENT
// ============================================================================

async function fetchAccounts(): Promise<{ accounts: string[] }> {
    if (!currentState.currentRequisition) {
        throw new Error("No requisition data available");
    }

    const response = await fetch(`${CONFIG.WORKER_URL}/accounts?requisitionId=${currentState.currentRequisition.id}&isSandbox=${currentState.isSandbox}`, {
        headers: { "X-License-Key": currentState.licenseKey || "" }
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch accounts: HTTP ${response.status}`);
    }

    return await response.json();
}

async function saveAccountsToConfig(accountsData: { accounts: string[] }): Promise<void> {
    return await Excel.run(async (context) => {
        try {
            let configSheet: Excel.Worksheet | undefined;
            let sheetExists = false;

            try {
                const worksheets = context.workbook.worksheets;
                worksheets.load("items/name");
                await context.sync();

                const existingSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.CONFIG);
                if (existingSheet) {
                    configSheet = existingSheet;
                    sheetExists = true;
                } else {
                    configSheet = worksheets.add(CONFIG.SHEETS.CONFIG);
                    sheetExists = false;
                }

                await context.sync();
            } catch (error) {
                logOfficeError(error, "Error accessing/creating config sheet");
                throw error;
            }

            if (!configSheet) throw new Error("Could not create config sheet");

            if (!sheetExists) {
                const headers = [
                    [
                        "Requisition ID",
                        "Bank Name",
                        "Country",
                        "Account ID",
                        "Account Name",
                        "IBAN",
                        "Currency",
                        "Connected Date",
                    ],
                ];
                configSheet.getRange("A1:H1").values = headers;
                configSheet.getRange("A1:H1").format.font.bold = true;
                await context.sync();
            }

            const usedRange = configSheet.getUsedRange();
            usedRange.load("rowCount, values");
            await context.sync();

            const existingAccountIds = new Set<string>(usedRange.isNullObject ? [] : usedRange.values.slice(1).map(row => String(row[3])));
            const newAccounts = accountsData.accounts.filter(id => !existingAccountIds.has(id));
            const nextRow = usedRange.isNullObject ? 2 : usedRange.rowCount + 1;

            for (let i = 0; i < newAccounts.length; i++) {
                const accountId = newAccounts[i];
                const rowData = [
                    currentState.currentRequisition?.id || "",
                    currentState.selectedBank?.name || "",
                    currentState.selectedCountry || "",
                    accountId,
                    "",
                    "",
                    "",
                    "'" + new Date().toISOString().split("T")[0],
                ];

                configSheet.getRange(`A${nextRow + i}:H${nextRow + i}`).values = [rowData];
            }

            await context.sync();
        } catch (error) {
            logOfficeError(error, "Error in saveAccountsToConfig");
            throw error;
        }
    });
}

async function fetchAndSaveTransactions(): Promise<number> {
    if (!currentState.currentRequisition) {
        throw new Error("No requisition data available");
    }

    const response = await fetch(
        `${CONFIG.WORKER_URL}/all-transactions?requisitionId=${currentState.currentRequisition.id}&isSandbox=${currentState.isSandbox}`,
        { headers: { "X-License-Key": currentState.licenseKey || "" } }
    );
    if (!response.ok) {
        throw new Error(`Failed to fetch transactions: HTTP ${response.status}`);
    }

    const data = await response.json();
    if (data.errors?.length) {
        throw new Error("Bank connected, but transaction import was incomplete. Use Sync New Data to retry. " + data.errors.map((e: { detail?: string; summary?: string }) => e.detail || e.summary).join("; "));
    }
    const transactions = data.transactions || [];

    const accountMetadata = await getAccountMetaData();
    const transactionsWithContext = transactions.map((tx: Record<string, unknown>) => ({
        ...tx,
        resourceId: accountMetadata.find(a => a.accountId === tx.resourceId)?.transactionAccountId || tx.resourceId,
        bankName: currentState.selectedBank ? currentState.selectedBank.name : "Unknown Bank"
    }));

    const count = await saveNewTransactions(transactionsWithContext);
    if (accountMetadata.length > 0 && accountMetadata.every(a => a.requisitionId === currentState.currentRequisition!.id)) {
        await saveSyncResult(true, `Imported ${count} transactions.`);
    }
    return count;
}

async function refreshAllData(): Promise<void> {
    const refreshBtn = document.getElementById("refreshBtn") as HTMLButtonElement;
    const originalText = refreshBtn ? refreshBtn.innerHTML : "Sync New Data";

    try {
        if (refreshBtn) {
            refreshBtn.innerHTML = `⏳ ${translateFreeText("Refreshing...")}`;
            refreshBtn.disabled = true;
        }
        updateStatus("Refreshing data...");
        setProgress(10);

        const accounts = await getAccountMetaData();
        if (accounts.length === 0) {
            throw new Error("No connected accounts found");
        }

        let totalTransactions = 0;
        const errors: string[] = [];

        for (let i = 0; i < accounts.length; i++) {
            const acc = accounts[i];
            updateStatus(`Refreshing account ${i + 1} of ${accounts.length}...`);
            setProgress(25 + (i / accounts.length) * 50);

            let retryCount = 0;
            let success = false;

            while (retryCount < CONFIG.MAX_RETRIES && !success) {
                try {
                    const response = await fetch(`${CONFIG.WORKER_URL}/accounts/${acc.accountId}/transactions?isSandbox=${acc.isSandbox}&requisitionId=${encodeURIComponent(acc.requisitionId)}`, {
                        headers: { "X-License-Key": currentState.licenseKey || "" }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const transactions = data.transactions?.booked || [];

                        const enrichedTransactions = transactions.map((tx: Record<string, unknown>) => ({
                            ...tx,
                            resourceId: acc.transactionAccountId,
                            accountIban: data.accountIban,
                            bankName: acc.bankName,
                        }));

                        const newCount = await saveNewTransactions(enrichedTransactions);
                        totalTransactions += newCount;
                        success = true;
                    } else if (response.status === 409) {
                        updateStatus(`Bank is busy (409). Retrying ${retryCount + 1}/${CONFIG.MAX_RETRIES}...`);
                        retryCount++;
                        if (retryCount >= CONFIG.MAX_RETRIES) errors.push(`Account ${acc.accountId.substring(0, 8)}: Bank remained busy (409). Please retry later.`);
                        else await new Promise(r => setTimeout(r, CONFIG.RETRY_DELAY));
                    } else if (response.status === 429) {
                        errors.push(`Account ${acc.accountId.substring(0, 8)}: Rate limit exceeded (429). Try again tomorrow.`);
                        success = true;
                    } else if (response.status === 403) {
                        errors.push(`Account ${acc.accountId.substring(0, 8)}: Link expired or unauthorized (403). Please reconnect.`);
                        success = true;
                    } else if (response.status === 401) {
                        errors.push(`Account ${acc.accountId.substring(0, 8)}: Session invalid (401). Please login again.`);
                        success = true;
                    } else {
                        throw new Error(`HTTP ${response.status}`);
                    }
                } catch (error) {
                    console.error(`Error on account ${acc.accountId}:`, error);
                    retryCount++;
                    if (retryCount >= CONFIG.MAX_RETRIES) {
                        errors.push(`Account ${acc.accountId.substring(0, 8)}: ${(error as Error).message}`);
                    }
                    if (retryCount < CONFIG.MAX_RETRIES) await new Promise(r => setTimeout(r, CONFIG.RETRY_DELAY));
                }
            }
        }

        setProgress(100);
        let finalMsg = `${errors.length ? "Sync incomplete." : "Refresh complete!"} Added ${totalTransactions} new transactions.`;
        if (errors.length > 0) {
            finalMsg += ` Note: ${errors.length} accounts had issues.`;
            console.warn("Sync Issues:", errors);
        }
        await saveSyncResult(errors.length === 0, finalMsg + (errors.length ? " " + errors.join("; ") : ""));
        updateStatus(finalMsg);
        await updateDataSummary();
        if (errors.length) showError("Sync incomplete. " + errors.join("\n"));

        setTimeout(() => {
            setProgress(0);
            updateStatus("Ready");
        }, 5000);
    } catch (error) {
        await saveSyncResult(false, (error as Error).message).catch(e => console.error("Could not save failure status:", e));
        console.error("Error refreshing data:", error);
        showError("Failed to refresh data: " + (error as Error).message);
        updateStatus("Refresh failed");
        setProgress(0);
    } finally {
        if (refreshBtn) {
            refreshBtn.innerHTML = originalText;
            refreshBtn.disabled = false;
            if (window.lucide) window.lucide.createIcons();
        }
    }
}

async function getAccountMetaData(): Promise<AccountMetadata[]> {
    try {
        return await Excel.run(async (context) => {
            try {
                const worksheets = context.workbook.worksheets;
                worksheets.load("items/name");
                await context.sync();

                const configSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.CONFIG);
                if (!configSheet) return [];

                const range = configSheet.getUsedRange();
                range.load("values, rowCount");
                await context.sync();

                if (range.isNullObject || range.rowCount <= 1) return [];

                const data = range.values;
                const headers = data[0] as string[];

                const accountIdIdx = headers.indexOf("Account ID");
                const bankNameIdx = headers.indexOf("Bank Name");
                const countryIdx = headers.indexOf("Country");
                const reqIdIdx = headers.indexOf("Requisition ID");

                return data.slice(1).map((row) => ({
                    accountId: String(row[accountIdIdx] || ""),
                    transactionAccountId: String(row[headers.indexOf("Transaction Account ID")] || row[accountIdIdx] || ""),
                    bankName: String(row[bankNameIdx] || ""),
                    isSandbox: row[countryIdx] === 'SANDBOX',
                    requisitionId: String(row[reqIdIdx] || "")
                })).filter(acc => acc.accountId);
            } catch (error) {
                logOfficeError(error, "Error getting account metadata");
                return [];
            }
        });
    } catch (outerError) {
        logOfficeError(outerError, "Outer error getting account metadata");
        return [];
    }
}

async function saveNewTransactions(transactions: Record<string, unknown>[]): Promise<number> {
    if (!transactions || transactions.length === 0) return 0;

    return await Excel.run(async (context) => {
        try {
            // Phase 1: Get or create sheet and load initial data (single sync)
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");
            await context.sync();

            let transSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.TRANSACTIONS);
            let isNewSheet = false;
            if (!transSheet) {
                transSheet = worksheets.add(CONFIG.SHEETS.TRANSACTIONS);
                isNewSheet = true;
            }

            // Phase 2: Load headers and existing data in one sync
            const headerRange = transSheet.getRange("A1:ZZ1").getUsedRangeOrNullObject();
            const usedRange = transSheet.getUsedRange(true);
            headerRange.load("values");
            usedRange.load("values, rowCount");
            await context.sync();

            // Process headers
            let currentHeaders: string[] = [];
            if (!headerRange.isNullObject) {
                currentHeaders = headerRange.values[0] as string[];
            } else {
                currentHeaders = [...CORE_COLUMNS];
            }

            // Process transactions and determine final headers
            const processedTransactions = transactions.map((tx) => extractTransactionData(tx, String(tx.bankName || "Unknown Bank")));
            const allKeys = new Set(currentHeaders);
            processedTransactions.forEach(txObj => {
                Object.keys(txObj).forEach(key => allKeys.add(key));
            });

            const newKeys = Array.from(allKeys).filter(key => !currentHeaders.includes(key));
            const finalHeaders = [...currentHeaders, ...newKeys];
            const endColLetter = getExcelColumnLetter(finalHeaders.length);

            // Get existing unique keys for deduplication
            let existingData: unknown[][] = [];
            if (!usedRange.isNullObject && usedRange.rowCount > 1) {
                existingData = usedRange.values.slice(1);
            }

            const newTransactions = filterNewTransactions(processedTransactions, finalHeaders, existingData)
                .map(txObj => finalHeaders.map(col => txObj[col] !== undefined ? txObj[col] : ""));

            // Phase 3: Batch all write operations together
            if (newKeys.length > 0 || isNewSheet) {
                const newHeaderRange = transSheet.getRange(`A1:${endColLetter}1`);
                newHeaderRange.values = [finalHeaders];
                newHeaderRange.format.font.bold = true;
            }

            if (newTransactions.length > 0) {
                const nextRow = usedRange.isNullObject ? 2 : usedRange.rowCount + 1;
                const endRow = nextRow + newTransactions.length - 1;
                const insertRange = transSheet.getRange(`A${nextRow}:${endColLetter}${endRow}`);
                insertRange.values = newTransactions;

                // Sort by booking date (descending)
                const bookingDateIdx = finalHeaders.indexOf("Booking Date");
                if (bookingDateIdx !== -1) {
                    const sortRange = transSheet.getRange(`A2:${endColLetter}${endRow}`);
                    sortRange.sort.apply([{ key: bookingDateIdx, ascending: false }]);
                }
            }

            // Single final sync for all write operations
            await context.sync();

            // Phase 4: Optional table creation/update (separate try-catch to not fail main operation)
            if (newKeys.length > 0 || isNewSheet || newTransactions.length > 0) {
                try {
                    const tables = transSheet.tables;
                    tables.load("items/name");
                    await context.sync();

                    let table = tables.items.find(t => t.name === "TransactionTable");
                    const totalRows = (usedRange.isNullObject ? 1 : usedRange.rowCount) + newTransactions.length;

                    if (!table) {
                        const tableRange = transSheet.getRange(`A1:${endColLetter}${totalRows}`);
                        table = tables.add(tableRange, true);
                        table.name = "TransactionTable";
                        table.style = "TableStyleMedium2";
                    } else if (newTransactions.length > 0) {
                        table.resize(`A1:${endColLetter}${totalRows}`);
                    }

                    // Auto-fit columns to prevent hashtags (########) in date columns
                    transSheet.getUsedRange(true).format.autofitColumns();

                    await context.sync();
                } catch (e) {
                    console.warn("Table update/creation skipped:", (e as Error).message);
                }
            }

            return newTransactions.length;
        } catch (error) {
            logOfficeError(error, "Error in saveNewTransactions");
            throw error;
        }
    });
}

// ============================================================================
// CATEGORIZATION / MAPPING
// ============================================================================

async function createMappingSheet(): Promise<Excel.Worksheet | undefined> {
    return await Excel.run(async (context) => {
        try {
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");
            await context.sync();

            let mappingSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.MAPPING);

            if (!mappingSheet) {
                mappingSheet = worksheets.add(CONFIG.SHEETS.MAPPING);
                await context.sync();

                const headers = [["Creditor/Debtor Type", "Identifier", "Category", "Sub Category"]];
                const examples = [
                    ["", "Amazon", "Shopping", "Online"],
                    ["", "Netflix", "Entertainment", "Subscription"],
                    ["", "Spotify", "Entertainment", "Subscription"],
                    ["", "Starbucks", "Food & Drink", "Cafe"],
                    ["", "Shell", "Transport", "Fuel"],
                    ["", "Lidl", "Shopping", "Groceries"],
                    ["", "Salary", "Income", "Monthly"],
                    ["", "Rent", "Housing", "Rent"],
                ];

                mappingSheet.getRange("A1:D1").values = headers;
                mappingSheet.getRange("A1:D1").format.font.bold = true;

                const exampleRange = mappingSheet.getRange(`A2:D${examples.length + 1}`);
                exampleRange.values = examples;
                exampleRange.format.font.italic = true;
                exampleRange.format.font.color = "#666666";

                mappingSheet.getRange("A1:D" + (examples.length + 1)).format.autofitColumns();
                await context.sync();
            } else {
                const headerRange = mappingSheet.getRange("A1:A1");
                headerRange.load("values");
                await context.sync();

                if (headerRange.values[0][0] !== "Creditor/Debtor Type") {
                    const headers = [["Creditor/Debtor Type", "Identifier", "Category", "Sub Category"]];
                    mappingSheet.getRange("A1:D1").values = headers;
                    mappingSheet.getRange("A1:D1").format.font.bold = true;
                    await context.sync();
                }
            }

            return mappingSheet;
        } catch (error) {
            logOfficeError(error, "Error creating mapping sheet");
            throw error;
        }
    });
}

async function getMappingData(): Promise<MappingRule[]> {
    return await Excel.run(async (context) => {
        try {
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");
            await context.sync();

            const mappingSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.MAPPING);
            if (!mappingSheet) {
                console.log("Mapping sheet not found");
                return [];
            }

            const range = mappingSheet.getRange("A:D").getUsedRangeOrNullObject(true);
            range.load("values, isNullObject");
            await context.sync();

            if (range.isNullObject || !range.values || range.values.length === 0) {
                console.log("Mapping sheet is empty");
                return [];
            }

            const data = range.values.slice(1).filter((row) => {
                const hasData =
                    row &&
                    row.length > 0 &&
                    row.some((cell) => cell !== null && cell !== undefined && cell.toString().trim() !== "");
                return hasData;
            });

            const mappings = data
                .map((row) => ({
                    typeFilter: String(row[0] || ""),
                    identifier: String(row[1] || ""),
                    category: String(row[2] || ""),
                    subCategory: String(row[3] || ""),
                }))
                .filter((mapping) => {
                    const hasIdentifier = mapping.identifier && mapping.identifier.toString().trim() !== "";
                    return hasIdentifier;
                });

            return mappings;
        } catch (error) {
            logOfficeError(error, "Error getting mapping data");
            return [];
        }
    });
}

async function applyCategorization(): Promise<number> {
    return await Excel.run(async (context) => {
        try {
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");
            await context.sync();

            const transSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.TRANSACTIONS);
            if (!transSheet) {
                throw new Error("Transactions sheet not found");
            }

            await createMappingSheet();
            const mappings = await getMappingData();

            if (mappings.length === 0) {
                throw new Error(
                    "No mapping rules found. Please add rules to the Mapping sheet.\nFormat: Type | Identifier | Category | Sub Category",
                );
            }

            const dataRange = transSheet.getUsedRange(true);
            dataRange.load("values, rowCount");
            await context.sync();

            if (dataRange.isNullObject || dataRange.rowCount <= 1) {
                return 0;
            }

            const allData = dataRange.values;
            const headers = allData[0] as string[];

            const categoryCol = headers.indexOf("Category");
            const subCategoryCol = headers.indexOf("Sub Category");
            const typeCol = headers.indexOf("Type");
            const uniqueKeyCol = headers.indexOf("Unique Key");

            if (categoryCol === -1 || subCategoryCol === -1) {
                throw new Error("Category columns not found in Transactions sheet");
            }

            const columnsToExclude = [
                categoryCol,
                subCategoryCol,
                typeCol,
                uniqueKeyCol
            ];

            let categorizedCount = 0;

            for (let rowIndex = 1; rowIndex < allData.length; rowIndex++) {
                const row = allData[rowIndex];
                if (!row) continue;

                const transactionType = (typeCol !== -1 && row[typeCol]) ? row[typeCol].toString().toLowerCase() : "";

                const mapping = mappings.find((m) => {
                    if (m.typeFilter && m.typeFilter.trim() !== "") {
                        if (m.typeFilter.toLowerCase() !== transactionType) {
                            return false;
                        }
                    }

                    const identifier = m.identifier.toLowerCase();

                    for (let colIndex = 0; colIndex < headers.length; colIndex++) {
                        if (columnsToExclude.includes(colIndex)) continue;

                        const cellValue = (row[colIndex] || "").toString().toLowerCase();

                        if (cellValue.includes(identifier)) {
                            return true;
                        }
                    }
                    return false;
                });

                if (mapping) {
                    allData[rowIndex][categoryCol] = mapping.category;
                    allData[rowIndex][subCategoryCol] = mapping.subCategory;
                    categorizedCount++;
                } else {
                    // Clear category if no mapping matches (rule might have been removed)
                    allData[rowIndex][categoryCol] = "";
                    allData[rowIndex][subCategoryCol] = "";
                }
            }

            // Write only the two derived category columns, preserving formulas elsewhere.
            for (const column of [categoryCol, subCategoryCol]) {
                transSheet.getRangeByIndexes(1, column, allData.length - 1, 1).values =
                    allData.slice(1).map(row => [row[column]]);
            }
            await context.sync();

            return categorizedCount;
        } catch (error) {
            logOfficeError(error, "Error applying categorization");
            throw error;
        }
    });
}

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

function updateStatus(message: string): void {
    const statusText = document.getElementById("statusText");
    if (statusText) {
        statusText.textContent = translateFreeText(message);
    }
}

function setProgress(percentage: number): void {
    const progressBar = document.getElementById("progressBar");
    const progressText = document.getElementById("progressText");

    if (progressBar) {
        progressBar.style.width = `${percentage}%`;
    }

    if (progressText && percentage > 0) {
        progressText.textContent = `${percentage}%`;
    } else if (progressText) {
        progressText.textContent = "";
    }
}

// Store last error details for debugging
let lastErrorDetails: string = "";



function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]!));
}

function showError(message: string | Error | Record<string, unknown>, details?: string): void {
    console.error("Application Error:", message);
    if (details) {
        console.error("Error details:", details);
        lastErrorDetails = details;
    }

    const errorModal = document.getElementById("errorModal");
    const errorMessage = document.getElementById("errorMessage");

    if (errorMessage) {
        let displayMsg = "An unknown error occurred.";
        if (typeof message === 'string' && message.trim() !== "") {
            displayMsg = escapeHtml(translateFreeText(message)).replace(/\n/g, '<br>');
        } else if (message instanceof Error) {
            displayMsg = escapeHtml(translateFreeText(message.message || "Native Error occurred"));
        } else if (message && typeof message === 'object') {
            displayMsg = translateFreeText(String((message as Record<string, unknown>).error || (message as Record<string, unknown>).message || JSON.stringify(message)));
        }

        // Add details if available (for debugging)
        if (details) {
            displayMsg += `<br><br><details><summary style="cursor:pointer;color:#64748b;font-size:0.8rem;">${translateFreeText("Show technical details")}</summary><pre style="font-size:0.7rem;background:#f1f5f9;padding:0.5rem;border-radius:4px;overflow-x:auto;max-height:150px;margin-top:0.5rem;">${escapeHtml(details.substring(0, 1000))}</pre></details>`;
        }

        errorMessage.innerHTML = displayMsg;
    }

    if (errorModal) {
        errorModal.classList.remove("hidden");
    }
    if (window.lucide) window.lucide.createIcons();
}

function clearSettingsModalCloseTimer(): void {
    if (settingsModalCloseTimer) {
        clearTimeout(settingsModalCloseTimer);
        settingsModalCloseTimer = null;
    }
}

function openModal(modalId: string): void {
    if (modalId === "settingsModal") {
        clearSettingsModalCloseTimer();
    }
    const modal = document.getElementById(modalId);
    if (modal) {
        modal.classList.remove("hidden");
    }
    if (window.lucide) window.lucide.createIcons();
}

function openExternalLink(pathOrUrl: string): void {
    const url = pathOrUrl.startsWith("http")
        ? pathOrUrl
        : `${window.location.origin}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;

    try {
        Office.context.ui.openBrowserWindow(url);
    } catch {
        window.open(url, "_blank", "noopener,noreferrer");
    }
}

function closeModal(modalId: string): void {
    if (modalId === "settingsModal") {
        clearSettingsModalCloseTimer();
    }
    if (modalId === 'connectionModal' && pendingRenewal && pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
    const modal = document.getElementById(modalId);
    if (modal) {
        modal.classList.add("hidden");
    }
}

// ============================================================================
// CONNECTED BANKS MANAGEMENT
// ============================================================================

async function loadConnectedBanks(): Promise<void> {
    try {
        const connectedBanks = await getConnectedBanksFromConfig();
        const connectedBanksContainer = document.getElementById("connectedBanks");

        if (!connectedBanksContainer) return;

        if (connectedBanks.length === 0) {
            connectedBanksContainer.innerHTML = `
                <div class="empty-state">
                    <i data-lucide="building-2"></i>
                    <p>No banks connected yet</p>
                    <button class="btn-secondary" onclick="switchView('connect')">
                        Connect Your First Bank
                    </button>
                </div>
            `;
            if (window.lucide) window.lucide.createIcons();
            return;
        }

        renderBankCards(connectedBanks);

        connectedBanks.forEach(async (bank) => {
            const cached = currentState.statusCache[bank.id];
            if (cached && (Date.now() - cached.timestamp < CONFIG.STATUS_CACHE_TTL)) {
                updateBankCardStatus(bank.id, cached.data as Record<string, unknown>);
                return;
            }

            try {
                const isSandbox = bank.country === 'SANDBOX';
                const response = await fetch(`${CONFIG.WORKER_URL}/requisitions/${bank.id}?isSandbox=${isSandbox}`, {
                    headers: { "X-License-Key": currentState.licenseKey || "" }
                });
                if (response.ok) {
                    const statusData = await response.json();

                    currentState.statusCache[bank.id] = {
                        data: statusData,
                        timestamp: Date.now()
                    };

                    updateBankCardStatus(bank.id, statusData);
                } else if (response.status === 404) {
                    updateBankCardStatus(bank.id, { deleted: true });
                }
            } catch (error) {
                console.error(`Error fetching status for bank ${bank.id}:`, error);
            }
        });

        await updateDataSummary();
    } catch (error) {
        console.error("Error loading connected banks:", error);
    }
}

function renderBankCards(banks: ConnectedBank[]): void {
    const container = document.getElementById("connectedBanks");
    if (!container) return;

    container.innerHTML = banks.map(bank => `
        <div class="connected-bank-card" id="bank-card-${bank.id}">
            <div class="bank-header">
                <div class="bank-title">
                    <strong>${bank.bankName}</strong>
                    <span class="country-badge">${bank.country}</span>
                </div>
                <div class="bank-actions">
                    <button class="btn-icon-danger" onclick="deleteConnection('${bank.id}')" title="Remove Connection">
                        <i data-lucide="trash-2"></i>
                    </button>
                </div>
            </div>
            <div class="bank-body">
                <div class="bank-stat-row">
                    <span class="bank-label">Accounts</span>
                    <span class="bank-value">${bank.accountCount} detected</span>
                </div>
                <div class="bank-stat-row" id="validity-${bank.id}">
                    <span class="bank-label">Status</span>
                    <span class="bank-value text-muted">Checking...</span>
                </div>
            </div>
            <div class="bank-footer" id="footer-${bank.id}">
                <small>Connected ${new Date(bank.connectedAt).toLocaleDateString()}</small>
                <button class="btn-reconnect" onclick="reconnectBank('${bank.id}')">${translateFreeText("Renew bank access")}</button>
            </div>
        </div>
    `).join("");
    if (window.lucide) window.lucide.createIcons();
}

function updateBankCardStatus(requisitionId: string, data: Record<string, unknown>): void {
    const validityEl = document.getElementById(`validity-${requisitionId}`);
    if (!validityEl) return;
    const expiry = consentExpiry(data.agreement_details as Record<string, unknown> | undefined);
    const expired = data.status === 'EX' || (expiry !== null && expiry <= Date.now());
    const status = STATUS_MAP[data.status as string];
    const label = data.deleted ? 'Disconnected' : expired ? 'Expired' : data.status === 'LN' ? 'Active' : status?.label || 'Checking...';
    const health = expired || data.deleted ? 'health-expired' : data.status === 'LN' ? 'health-good' : 'health-warning';
    const color = expired || data.deleted ? 'text-danger' : data.status === 'LN' ? 'text-success' : 'text-warning';
    validityEl.innerHTML = `<span class="health-dot ${health}"></span><span class="bank-label">Status</span><span class="bank-value ${color}">${translateFreeText(label)}</span>`;
    if (!expired && data.status === 'LN' && expiry !== null) {
        const days = Math.ceil((expiry - Date.now()) / 86400000);
        validityEl.querySelector('.bank-value')!.textContent = currentState.language === 'de' ? `Noch ${days} Tage` : `${days} days remaining`;
    }
}

async function deleteConnection(requisitionId: string): Promise<void> {
    if (pendingRenewal || renewalStarting) {
        showError("Finish the pending bank renewal before removing a connection.");
        return;
    }
    if (!confirm("Are you sure you want to remove this bank connection? This will also remove the records from your config sheet.")) return;

    try {
        updateStatus("Removing connection...");

        const allAccs = await getAccountMetaData();
        const thisBank = allAccs.find(a => a.requisitionId === requisitionId);
        const bankName = thisBank ? thisBank.bankName : 'Unknown';
        const isSandbox = thisBank ? thisBank.isSandbox : false;

        const response = await fetch(`${CONFIG.WORKER_URL}/requisitions/${requisitionId}?bankName=${encodeURIComponent(bankName)}&isSandbox=${isSandbox}`, {
            method: 'DELETE',
            headers: { "X-License-Key": currentState.licenseKey || "" }
        });

        if (response.ok || response.status === 404) {
            await Excel.run(async (context) => {
                const configSheet = context.workbook.worksheets.getItem(CONFIG.SHEETS.CONFIG);
                const range = configSheet.getUsedRange();
                range.load("values");
                await context.sync();

                const data = range.values;
                const headers = data[0] as string[];
                const reqIdIdx = headers.indexOf("Requisition ID");

                const newData = data.filter((row, idx) => idx === 0 || row[reqIdIdx] !== requisitionId);

                configSheet.getUsedRange().clear();
                const newRange = configSheet.getRange(`A1:${getExcelColumnLetter(headers.length)}${newData.length}`);
                newRange.values = newData;
                newRange.getRow(0).format.font.bold = true;
                await context.sync();
            });

            updateStatus("Connection removed successfully.");
            await loadConnectedBanks();
        } else {
            throw new Error("Failed to delete connection on server.");
        }
    } catch (error) {
        console.error("Error deleting connection:", error);
        showError("Failed to delete connection: " + (error as Error).message);
    }
}

async function bankApi(path: string, init: RequestInit = {}): Promise<any> {
    const response = await fetch(`${CONFIG.WORKER_URL}${path}`, {
        ...init, headers: { "Content-Type": "application/json", "X-License-Key": currentState.licenseKey || "" }
    });
    const data = await response.json();
    if (!response.ok) throw Object.assign(new Error(data.detail || `HTTP ${response.status}`), { status: response.status });
    return data;
}

async function cancelRenewal(): Promise<void> {
    if (!pendingRenewal || currentState.isAuthCompleting) return;
    try {
        Office.context.document.settings.remove(RENEWAL_SETTING);
        await saveDocumentSettingsAsync();
        closeModal('connectionModal');
        pendingRenewal = null;
        currentState.currentRequisition = null;
        updateStatus('Renewal cancelled. Your existing connection has been kept.');
    } catch (error) { showError((error as Error).message); }
}

function activateRenewal(pending: PendingRenewal): void {
    currentState.currentRequisition = pending.requisition;
    currentState.selectedBank = pending.institution;
    currentState.selectedCountry = pending.bank.country;
    currentState.isSandbox = pending.bank.country === 'SANDBOX';
    showConnectionModal(pending.requisition.link || '');
}

async function reconnectBank(requisitionId: string): Promise<void> {
    if (renewalStarting || currentState.isAuthCompleting) return;
    if (pendingRenewal) {
        if (pendingRenewal.oldRequisitionId !== requisitionId) {
            showError("Finish the pending bank renewal first.");
            return;
        }
        renewalStarting = true;
        try {
            const status = await bankApi(`/requisitions/${pendingRenewal.requisition.id}?isSandbox=${pendingRenewal.bank.country === 'SANDBOX'}`);
            if (status.status === 'EX' || status.status === 'RJ') {
                Office.context.document.settings.remove(RENEWAL_SETTING);
                await saveDocumentSettingsAsync();
                pendingRenewal = null;
            } else {
                activateRenewal(pendingRenewal);
                return;
            }
        } catch (error) {
            if ((error as Error & { status?: number }).status === 404) {
                Office.context.document.settings.remove(RENEWAL_SETTING);
                await saveDocumentSettingsAsync();
                pendingRenewal = null;
            } else {
                showError((error as Error).message);
                return;
            }
        } finally {
            renewalStarting = false;
        }
    }
    renewalStarting = true;
    try {
        const banks = await getConnectedBanksFromConfig();
        const bank = banks.find(b => b.id === requisitionId);
        if (!bank) throw new Error('The bank connection was not found in this workbook.');
        if (bank.country !== 'SANDBOX' && !currentState.licenseKey) {
            openModal('settingsModal');
            throw new Error('Enter your subscription key before renewing bank access.');
        }
        updateStatus("Renewing bank access...");
        const previous = (await getAccountMetaData()).filter(a => a.requisitionId === requisitionId);
        const previousAccounts: RenewalAccount[] = await Promise.all(previous.map(async account => {
            const metadata = await bankApi(`/accounts/${account.accountId}/metadata?isSandbox=${account.isSandbox}&requisitionId=${encodeURIComponent(account.requisitionId)}`);
            return { id: account.accountId, iban: metadata.iban || '', name: metadata.name || '' };
        }));
        const result = await bankApi('/renew-connection', {
            method: 'POST', body: JSON.stringify({ requisition_id: requisitionId, bank_name: bank.bankName,
                user_language: currentState.language.toUpperCase() })
        });
        if (!result.id || !result.link || !result.institution) throw new Error('The bank did not return an authorization link.');
        pendingRenewal = { oldRequisitionId: requisitionId, bank, requisition: { id: result.id, link: result.link },
            institution: result.institution, previousAccounts };
        Office.context.document.settings.set(RENEWAL_SETTING, pendingRenewal);
        await saveDocumentSettingsAsync();
        activateRenewal(pendingRenewal);
    } catch (error) {
        showError((error as Error).message);
        updateStatus('Renewal could not be started. Your existing connection has been kept.');
    } finally {
        renewalStarting = false;
    }
}

async function completeRenewal(): Promise<void> {
    if (!pendingRenewal || currentState.isAuthCompleting) return;
    currentState.isAuthCompleting = true;
    const pending = pendingRenewal;
    if (pollingInterval) { clearInterval(pollingInterval); pollingInterval = null; }
    try {
        const sandbox = pending.bank.country === 'SANDBOX';
        const linked = await bankApi(`/requisitions/${pending.requisition.id}?isSandbox=${sandbox}`);
        if (linked.status !== 'LN' || !Array.isArray(linked.accounts) || !linked.accounts.length) {
            throw new Error('Please finish authorization in your browser first.');
        }
        const accounts: RenewalAccount[] = await Promise.all(linked.accounts.map(async (id: string) => {
            const metadata = await bankApi(`/accounts/${id}/metadata?isSandbox=${sandbox}&requisitionId=${encodeURIComponent(pending.requisition.id)}`);
            return { id, iban: metadata.iban || '', name: metadata.name || '' };
        }));
        const matches = matchRenewedAccounts(pending.previousAccounts, accounts);
        await Excel.run(async context => {
            const sheet = context.workbook.worksheets.getItem(CONFIG.SHEETS.CONFIG);
            const range = sheet.getUsedRange();
            range.load('values,rowIndex,columnIndex');
            await context.sync();
            const headers = range.values[0] as string[];
            const req = headers.indexOf('Requisition ID');
            const acc = headers.indexOf('Account ID');
            const iban = headers.indexOf('IBAN');
            const name = headers.indexOf('Account Name');
            const date = headers.indexOf('Connected Date');
            if ([req, acc, iban, name, date, headers.indexOf('Bank Name'), headers.indexOf('Country')].some(i => i < 0)) throw new Error('BankConfig headers are missing.');
            const oldRows = range.values.map((row, index) => ({ row, index })).filter(item => item.row[req] === pending.oldRequisitionId);
            // A settings-save retry after a successful worksheet write must not duplicate rows.
            if (!oldRows.length && range.values.some(row => row[req] === pending.requisition.id)) return;
            if (!oldRows.length) throw new Error('The original connection has changed. Please reopen the add-in.');
            let stable = headers.indexOf('Transaction Account ID');
            if (stable < 0) {
                stable = headers.length;
                sheet.getCell(range.rowIndex, range.columnIndex + stable).values = [['Transaction Account ID']];
            }
            for (let i = 0; i < accounts.length; i++) {
                const account = accounts[i];
                const previousId = matches.get(account.id);
                const source = oldRows.find(item => item.row[acc] === previousId)?.row;
                const row = new Array(Math.max(headers.length, stable + 1)).fill('');
                if (source) source.forEach((value, column) => row[column] = value);
                row[req] = pending.requisition.id;
                row[acc] = account.id;
                row[headers.indexOf('Bank Name')] = pending.bank.bankName;
                row[headers.indexOf('Country')] = pending.bank.country;
                row[iban] = account.iban;
                row[name] = source?.[name] || account.name;
                row[date] = "'" + new Date().toISOString().split('T')[0];
                row[stable] = source?.[stable] || previousId || account.id;
                const target = oldRows[i]?.index ?? range.values.length + i - oldRows.length;
                sheet.getRangeByIndexes(range.rowIndex + target, range.columnIndex, 1, row.length).values = [row];
            }
            for (const item of oldRows.slice(accounts.length)) {
                sheet.getRangeByIndexes(range.rowIndex + item.index, range.columnIndex, 1, Math.max(headers.length, stable + 1)).clear(Excel.ClearApplyTo.contents);
            }
            await context.sync();
        });
        Office.context.document.settings.remove(RENEWAL_SETTING);
        await saveDocumentSettingsAsync();
        pendingRenewal = null;
        currentState.currentRequisition = null;
        delete currentState.statusCache[pending.oldRequisitionId];
        closeModal('connectionModal');
        switchView('dashboard');
        updateStatus('Bank access renewed. Use Sync New Data to fetch transactions.');
    } catch (error) {
        showError((error as Error).message);
    } finally {
        currentState.isAuthCompleting = false;
    }
}

async function getConnectedBanksFromConfig(): Promise<ConnectedBank[]> {
    try {
        return await Excel.run(async (context) => {
            try {
                const worksheets = context.workbook.worksheets;
                worksheets.load("items/name");
                await context.sync();

                const configSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.CONFIG);
                if (!configSheet) {
                    return [];
                }

                const range = configSheet.getUsedRange();
                range.load("values, rowCount");
                await context.sync();

                if (range.isNullObject || range.rowCount <= 1) {
                    return [];
                }

                const data = range.values;
                if (!data || data.length <= 1) {
                    return [];
                }

                const headers = data[0] as string[];
                const reqIdIdx = headers.indexOf("Requisition ID");
                const bankNameIdx = headers.indexOf("Bank Name");
                const countryIdx = headers.indexOf("Country");
                const connectedAtIdx = headers.indexOf("Connected Date");

                const banksMap = new Map<string, ConnectedBank>();

                data.slice(1).forEach((row) => {
                    const requisitionId = reqIdIdx !== -1 ? String(row[reqIdIdx]) : String(row[0]);
                    const bankName = bankNameIdx !== -1 ? String(row[bankNameIdx]) : String(row[1]);
                    const country = countryIdx !== -1 ? String(row[countryIdx]) : String(row[2]);
                    let connectedAt = connectedAtIdx !== -1 ? row[connectedAtIdx] : row[7];

                    if (typeof connectedAt === 'number') {
                        connectedAt = new Date(Date.UTC(1899, 11, 30) + connectedAt * 86400000).toISOString();
                    }

                    if (requisitionId && bankName) {
                        if (!banksMap.has(requisitionId)) {
                            banksMap.set(requisitionId, {
                                id: requisitionId,
                                bankName,
                                country,
                                accountCount: 0,
                                connectedAt: String(connectedAt) || new Date().toISOString(),
                            });
                        }
                        const bank = banksMap.get(requisitionId);
                        if (bank) bank.accountCount++;
                    }
                });

                return Array.from(banksMap.values());
            } catch (innerError) {
                logOfficeError(innerError, "Inner error getting connected banks");
                return [];
            }
        });
    } catch (outerError) {
        logOfficeError(outerError, "Outer error getting connected banks");
        return [];
    }
}

let commandNoticeReadInFlight = false;
let lastCommandNotice = '';
async function updateCommandNotice(): Promise<void> {
    if (commandNoticeReadInFlight || currentState.isAuthCompleting || renewalStarting) return;
    commandNoticeReadInFlight = true;
    try {
        const sync = await readSyncResult();
        const notice = Office.context.document.settings.get('bankCommandNotice') as { at: string; title: string; message: string } | undefined;
        if (notice && notice.at !== lastCommandNotice) {
            lastCommandNotice = notice.at;
            updateStatus(`${notice.title}: ${notice.message}`);
            const status = document.getElementById('syncResultMessage');
            if (status && sync) status.textContent = sync.message;
            const lastSync = document.getElementById('statLastSync');
            if (lastSync) lastSync.textContent = sync?.lastSuccess ? new Date(sync.lastSuccess).toLocaleString() : 'Never';
        }
    } catch (error) { console.error('Could not refresh command status:', error); }
    finally { commandNoticeReadInFlight = false; }
}

async function updateDataSummary(): Promise<void> {
    try {
        const summary = await getDataSummary();

        const statTransactions = document.getElementById("statTransactions");
        const statAccounts = document.getElementById("statAccounts");
        const statLastSync = document.getElementById("statLastSync");

        if (statTransactions) statTransactions.textContent = String(summary.totalTransactions);
        if (statAccounts) statAccounts.textContent = String(summary.totalAccounts);
        if (statLastSync) statLastSync.textContent = summary.lastUpdated;
        const syncMessage = document.getElementById("syncResultMessage");
        if (syncMessage) syncMessage.textContent = summary.syncMessage || "No successful sync has been recorded yet.";
    } catch (error) {
        console.error("Error updating data summary:", error);
    }
}

async function getDataSummary(): Promise<{ totalAccounts: number; totalTransactions: number; lastUpdated: string; syncMessage?: string }> {
    return await Excel.run(async (context) => {
        try {
            let totalAccounts = 0;
            let totalTransactions = 0;

            try {
                const worksheets = context.workbook.worksheets;
                worksheets.load("items/name");
                await context.sync();

                const configSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.CONFIG);
                if (configSheet) {
                    const configRange = configSheet.getUsedRange();
                    configRange.load("rowCount,values");
                    await context.sync();

                    if (!configRange.isNullObject && configRange.rowCount > 1) {
                        const accountColumn = configRange.values[0].indexOf('Account ID');
                        totalAccounts = configRange.values.slice(1).filter(row => accountColumn >= 0 && row[accountColumn]).length;
                    }
                }
            } catch {
                totalAccounts = 0;
            }

            try {
                const worksheets = context.workbook.worksheets;
                worksheets.load("items/name");
                await context.sync();

                const transSheet = worksheets.items.find((sheet) => sheet.name === CONFIG.SHEETS.TRANSACTIONS);
                if (transSheet) {
                    const transRange = transSheet.getUsedRange(true);
                    transRange.load("rowCount");
                    await context.sync();

                    if (!transRange.isNullObject && transRange.rowCount > 1) {
                        totalTransactions = transRange.rowCount - 1;
                    }
                }
            } catch {
                totalTransactions = 0;
            }

            const sync = await readSyncResult();
            const lastUpdated = sync?.lastSuccess ? new Date(sync.lastSuccess).toLocaleString() : "Never";

            return {
                totalAccounts: Math.max(0, totalAccounts),
                totalTransactions: Math.max(0, totalTransactions),
                lastUpdated,
                syncMessage: sync?.message,
            };
        } catch (error) {
            logOfficeError(error, "Error getting data summary");
            return {
                totalAccounts: 0,
                totalTransactions: 0,
                lastUpdated: "Unknown",
            };
        }
    });
}

// ============================================================================
// UI WRAPPER FUNCTIONS FOR BUTTONS
// ============================================================================

async function applyMappingRules(): Promise<void> {
    const btnEl = document.getElementById("applyMappingBtn") as HTMLButtonElement;
    const originalText = btnEl ? btnEl.innerHTML : "Apply Mapping Rules";

    try {
        if (btnEl) {
            btnEl.innerHTML = `⏳ ${translateFreeText("Applying...")}`;
            btnEl.disabled = true;
        }
        updateStatus("Applying mapping rules...");
        setProgress(25);

        const categorizedCount = await applyCategorization();

        setProgress(100);
        updateStatus(`Applied categories to ${categorizedCount} transactions!`);

        setTimeout(() => {
            setProgress(0);
            updateStatus("Ready");
        }, 2000);
    } catch (error) {
        console.error("Error applying mapping rules:", error);

        if ((error as Error).message.includes("No mapping rules found")) {
            showError(
                `No mapping rules found!\n\nTo use categorization:\n1. Click "Open Mapping Sheet" first\n2. Add rules in the Mapping sheet:\n   • Column A: Type filter (Optional)\n   • Column B: Identifier (e.g., "AMAZON")\n   • Column C: Category (e.g., "Shopping")\n   • Column D: Sub Category (e.g., "Online")\n3. Click "Categorize Transactions" again`,
            );
        } else {
            showError(`Failed to apply categories: ${(error as Error).message}`);
        }

        updateStatus("Error applying categories");
        setProgress(0);
    } finally {
        if (btnEl) {
            btnEl.innerHTML = originalText;
            btnEl.disabled = false;
            if (window.lucide) window.lucide.createIcons();
        }
    }
}

async function createMappingSheetUI(): Promise<void> {
    const btnEl = document.getElementById("createMappingBtn") as HTMLButtonElement;
    const originalText = btnEl ? btnEl.innerHTML : "Create Mapping Sheet";

    try {
        if (btnEl) {
            btnEl.innerHTML = `⏳ ${translateFreeText("Opening...")}`;
            btnEl.disabled = true;
        }
        updateStatus("Opening mapping sheet...");

        await createMappingSheet();

        await Excel.run(async (context) => {
            const mappingSheet = context.workbook.worksheets.getItem(CONFIG.SHEETS.MAPPING);
            mappingSheet.activate();
            await context.sync();
        });

        updateStatus('Mapping sheet ready! Add your rules and click "Categorize"');
    } catch (error) {
        console.error("Error creating mapping sheet:", error);
        showError(`Failed to open mapping sheet: ${(error as Error).message}`);
        updateStatus("Error opening mapping sheet");
    } finally {
        if (btnEl) {
            btnEl.innerHTML = originalText;
            btnEl.disabled = false;
            if (window.lucide) window.lucide.createIcons();
        }
    }
}

// ============================================================================
// LICENSE MANAGEMENT
// ============================================================================

async function saveLicenseKey(): Promise<void> {
    const input = document.getElementById('licenseKeyInput') as HTMLInputElement | null;
    const btn = document.getElementById('saveLicenseBtn') as HTMLButtonElement | null;
    if (!input || !btn) return;

    const key = input.value.trim();

    if (!key) {
        if (!currentState.licenseKey) {
            updateLicenseUiState(null, "unset");
        }
        updateStatus("No key entered. You can continue using Demo Mode.");
        return;
    }

    try {
        btn.disabled = true;
        btn.innerHTML = '⏳';
        updateStatus("Validating license...");

        const response = await fetch(`${CONFIG.WORKER_URL}/validate-license`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-License-Key': key
            }
        });

        const result = await response.json();

        if (result.valid) {
            Office.context.document.settings.set("licenseKey", key);
            await saveDocumentSettingsAsync();

            currentState.licenseKey = key;
            updateLicenseUiState(key, "active");
            updateStatus("License saved successfully!");
            clearSettingsModalCloseTimer();
            settingsModalCloseTimer = setTimeout(() => {
                closeModal('settingsModal');
                settingsModalCloseTimer = null;
            }, 1000);
        } else {
            if (currentState.licenseKey) {
                updateLicenseUiState(currentState.licenseKey, "active");
            } else {
                updateLicenseUiState(null, "invalid");
            }
            showError(result.error || "Invalid or inactive license key.");
        }
    } catch (error) {
        console.error("Error saving license:", error);
        showError("Failed to validate license: " + (error as Error).message);
    } finally {
        btn.disabled = false;
        btn.innerHTML = '<i data-lucide="save"></i>';
        if (window.lucide) window.lucide.createIcons();
    }
}

function saveDocumentSettingsAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
        Office.context.document.settings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve();
            } else {
                reject(new Error(result.error?.message || "Failed to save document settings."));
            }
        });
    });
}

function refreshDocumentSettingsAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
        Office.context.document.settings.refreshAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve();
            } else {
                reject(new Error(result.error?.message || "Failed to refresh document settings."));
            }
        });
    });
}

function updateLicenseUiState(key: string | null, statusType: "active" | "invalid" | "unset" = key ? "active" : "unset"): void {
    const status = document.getElementById('licenseStatus');
    const input = document.getElementById('licenseKeyInput') as HTMLInputElement | null;
    const removeBtn = document.getElementById('removeLicenseBtn') as HTMLButtonElement | null;
    const cancelLink = document.getElementById('cancelSubscriptionLink') as HTMLAnchorElement | null;

    if (input) {
        input.value = key || "";
    }

    if (status) {
        if (statusType === "active") {
            status.className = 'badge badge-success';
            status.textContent = translateFreeText('Active');
        } else if (statusType === "invalid") {
            status.className = 'badge badge-error';
            status.textContent = translateFreeText('Invalid');
        } else {
            status.className = 'badge badge-muted';
            status.textContent = translateFreeText('Not set');
        }
    }

    if (removeBtn) {
        removeBtn.disabled = !key;
    }

    if (cancelLink) {
        cancelLink.style.display = key ? "none" : "inline";
    }
}

async function removeLicenseKey(): Promise<void> {
    const btn = document.getElementById('removeLicenseBtn') as HTMLButtonElement | null;
    const input = document.getElementById('licenseKeyInput') as HTMLInputElement | null;
    if (!btn) return;

    clearSettingsModalCloseTimer();

    const localInputValue = input?.value.trim() || "";
    if (!currentState.licenseKey && !localInputValue) {
        updateLicenseUiState(null, "unset");
        updateStatus("No saved license key found.");
        return;
    }

    try {
        btn.disabled = true;
        btn.setAttribute("aria-busy", "true");

        await refreshDocumentSettingsAsync();
        Office.context.document.settings.remove("licenseKey");
        await saveDocumentSettingsAsync();
        await refreshDocumentSettingsAsync();

        let persistedKey = (Office.context.document.settings.get("licenseKey") as string | null) || null;
        if (persistedKey) {
            // Fallback for hosts that do not persist remove() reliably on first attempt.
            Office.context.document.settings.set("licenseKey", "");
            await saveDocumentSettingsAsync();
            await refreshDocumentSettingsAsync();
            persistedKey = (Office.context.document.settings.get("licenseKey") as string | null) || null;
        }

        currentState.licenseKey = persistedKey || null;
        updateLicenseUiState(currentState.licenseKey, currentState.licenseKey ? "active" : "unset");

        if (currentState.licenseKey) {
            throw new Error("Key is still present after save. Please try once more.");
        }

        updateStatus("Signed out. License key removed.");
    } catch (error) {
        console.error("Error removing license:", error);
        showError("Failed to remove license key: " + (error as Error).message);
    } finally {
        btn.removeAttribute("aria-busy");
        btn.disabled = !currentState.licenseKey;
    }
}

async function loadLicenseKey(): Promise<string | null> {
    try {
        await refreshDocumentSettingsAsync();
        const key = Office.context.document.settings.get("licenseKey") as string | null;
        currentState.licenseKey = key || null;
    } catch (error) {
        console.error("Error loading license:", error);
    }

    updateLicenseUiState(currentState.licenseKey, currentState.licenseKey ? "active" : "unset");
    return currentState.licenseKey;
}

// ============================================================================
// AUTOMATIC STATUS POLLING
// ============================================================================

function startPollingStatus(requisitionId: string): void {
    if (pollingInterval) clearInterval(pollingInterval);

    const statusContainer = document.getElementById('liveStatusContainer');
    const statusText = document.getElementById('liveStatusText');

    if (statusContainer) statusContainer.classList.remove('hidden');
    if (statusText) {
        statusText.textContent = translateFreeText("Waiting for authorization...");
    }

    pollingInterval = setInterval(async () => {
        try {
            const response = await fetch(
                `${CONFIG.WORKER_URL}/requisitions/${requisitionId}?isSandbox=${currentState.isSandbox}`,
                {
                    headers: { "X-License-Key": currentState.licenseKey || "" }
                }
            );

            if (!response.ok) {
                console.log(`Polling response: ${response.status}`);
                return;
            }

            const data = await response.json();
            if (currentState.currentRequisition?.id !== requisitionId) return;
            const status = data.status;
            console.log(`Requisition status: ${status}`);
            const statusObj = STATUS_MAP[status] || { label: status, desc: 'Processing...', color: 'text-muted' };

            const liveStatusText = document.getElementById('liveStatusText');
            const liveStatusDesc = document.getElementById('liveStatusDesc');

            if (liveStatusText) {
                liveStatusText.textContent = translateFreeText(statusObj.label);
            }
            if (liveStatusDesc) {
                liveStatusDesc.textContent = translateFreeText(statusObj.desc);
            }

            if (status === 'LN') {
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    pollingInterval = null;
                }

                if (liveStatusText) {
                    liveStatusText.textContent = translateFreeText("Connection Successful!");
                    liveStatusText.className = "text-success";
                }
                if (liveStatusDesc) liveStatusDesc.textContent = "Accounts linked. Completing setup...";

                await completeAuthorization();

            } else if (status === 'EX' || status === 'RJ') {
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    pollingInterval = null;
                }
                if (liveStatusText) {
                    liveStatusText.textContent = status === 'EX' ? translateFreeText("Link Expired") : translateFreeText("Connection Rejected");
                    liveStatusText.className = "text-danger";
                }
                showError(status === 'EX'
                    ? "The authorization link has expired. Please try connecting again."
                    : "The authorization was rejected by the bank or cancelled.");
            }
        } catch (error) {
            console.error("Status polling error:", error);
        }
    }, CONFIG.POLLING_INTERVAL);
}

// ============================================================================
// MANUAL AUTHORIZATION COMPLETION
// ============================================================================

async function manualCompleteAuth(): Promise<void> {
    const btn = document.getElementById('manualCompleteBtn') as HTMLButtonElement;
    if (btn) {
        btn.innerHTML = `⏳ ${translateFreeText("Checking...")}`;
        btn.disabled = true;
    }

    try {
        // First, check the current status
        if (currentState.currentRequisition) {
            const response = await fetch(
                `${CONFIG.WORKER_URL}/requisitions/${currentState.currentRequisition.id}?isSandbox=${currentState.isSandbox}`,
                {
                    headers: { "X-License-Key": currentState.licenseKey || "" }
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log("Manual check - Requisition status:", data.status);

                if (data.status === 'LN') {
                    // Already linked, proceed with completion
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                        pollingInterval = null;
                    }
                    await completeAuthorization();
                    return;
                } else if (data.status === 'EX' || data.status === 'RJ') {
                    showError(data.status === 'EX'
                        ? "The authorization link has expired. Please try again."
                        : "The authorization was rejected.");
                    if (btn) {
                        btn.innerHTML = `<i data-lucide="check-circle"></i> ${translateFreeText("I've Completed Authorization")}`;
                        btn.disabled = false;
                    }
                    if (window.lucide) window.lucide.createIcons();
                    return;
                } else {
                    // Not yet linked - show current status
                    const statusObj = STATUS_MAP[data.status] || { label: data.status, desc: 'Still processing...' };
                    showError(`Current status: ${translateFreeText(statusObj.label)}\n\n${translateFreeText(statusObj.desc)}\n\nPlease complete the authorization in your browser first.`);
                }
            } else {
                throw new Error(`Could not verify authorization (HTTP ${response.status}). Please retry.`);
            }
        } else {
            showError("No active connection request. Please start over.");
        }
    } catch (error) {
        console.error("Error in manual complete:", error);
        showError((error as Error).message);
    }

    if (btn) {
        btn.innerHTML = `<i data-lucide="check-circle"></i> ${translateFreeText("I've Completed Authorization")}`;
        btn.disabled = false;
    }
    if (window.lucide) window.lucide.createIcons();
}



// ============================================================================
// GLOBAL FUNCTION EXPORTS (for onclick handlers in HTML)
// ============================================================================

// Make functions available globally for HTML onclick handlers
(window as unknown as Record<string, unknown>).switchView = switchView;
(window as unknown as Record<string, unknown>).goToConnectStep = goToConnectStep;
(window as unknown as Record<string, unknown>).goToStep = goToStep;
(window as unknown as Record<string, unknown>).resetAndGoToStep1 = resetAndGoToStep1;
(window as unknown as Record<string, unknown>).selectBank = selectBank;
(window as unknown as Record<string, unknown>).refreshAllData = refreshAllData;
(window as unknown as Record<string, unknown>).createMappingSheetUI = createMappingSheetUI;
(window as unknown as Record<string, unknown>).applyMappingRules = applyMappingRules;
(window as unknown as Record<string, unknown>).deleteConnection = deleteConnection;
(window as unknown as Record<string, unknown>).reconnectBank = reconnectBank;
(window as unknown as Record<string, unknown>).openModal = openModal;
(window as unknown as Record<string, unknown>).closeModal = closeModal;
(window as unknown as Record<string, unknown>).openExternalLink = openExternalLink;
(window as unknown as Record<string, unknown>).saveLicenseKey = saveLicenseKey;
(window as unknown as Record<string, unknown>).removeLicenseKey = removeLicenseKey;
(window as unknown as Record<string, unknown>).manualCompleteAuth = manualCompleteAuth;

(window as unknown as Record<string, unknown>).cancelRenewal = cancelRenewal;
